// FICHE MAGASIN (SA-2) — mise en page validée de la maquette (CADRAGE-SuperAdmin « Fiche magasin ») :
// le fil d'Ariane seul sur sa ligne ; le titre de la section ouverte et les actions de la page (ouvrir le back-office,
// ajouter un équipement, bloquer) ; puis trois colonnes — les sept sections, leur contenu, et une carte d'identité
// courte et fixe. Les règles sans écran (disposition, sections, alerte unique, compteurs) vivent dans `store-page.ts`.
// ⚠️ §23.0 : tout vient de l'annuaire, de la base du magasin, de la supervision ou du journal — ce qui n'est pas branché
//    est dit (« à venir », « non mesuré ») ou absent, jamais simulé. Les écarts DÉCIDÉS avec la maquette sont écrits dans
//    `apps/console/CONVENTIONS-PRODUCTION.md`.
import { installableApplications } from '@orgazone/applications';
import { dateTime, dateTimeLong, dateTimeShort } from '@orgazone/date-format';
import { storeSyncMode, switchOn } from '@orgazone/sync-contract';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  api, type ActivityEvent, type BackupPiece, type BlockRow, type BoMessagingAccount, type BoStoreAccountProbe,
  type DeviceRow, type MigrationsStatus, type StoreAppsSurvey, type SupervisionStore, type TenantDevice, type TenantStore,
} from '../api';
import { EquipmentWizard, type EquipKind } from './EquipmentWizard';
import { DeviceHistoryModal, type HistoryTarget } from './device-history-modal';
import { DeviceForm, ReturnDevice } from './Fleet';
import { RentalModal } from './rental-modal';
import { transitOf } from '../rental-days';
import { SendRightCard } from './store-channel-allowance';
import { StoreUpdatesPanel } from './update-groups';
import { OpenBackofficeModal, ReasonModal } from './store-reason-modal';
import {
  blocksTouchingStore, counterTable, laggingDevice, layoutForWidth, overviewAlert, registerKey,
  sectionOf, STORE_SECTIONS, type StoreLayout, type StoreSection,
} from './store-page';
import { useAuth, can } from '../auth';
import { REGIME, fullEanRadical, storeStatus } from '../ui';
import { AUTO_BLOCK_RULES } from '../block-rules';
import { OFFLINE_RULE_DEFAULTS, offlineRuleError } from '../offline-rule';
import { syncModeOf } from '../sync-modes';
import { fmtDuration, healthTone, storeHealth, timeAgo, type StoreHealth } from '../sync-health';
import { NATURE_ICON } from '../equipment-types';
import { DeviceName, deviceRef } from '../device-name';
import { unitWord, type TitleValidity } from '../validity-field';
import { TableScroll } from '../table-scroll';
import { alert, codeCaisse, confirm, Copyable, EmptyState, Icon, Popover, rovingTarget, showToast, useLatest, type IconName } from '@orgazone/ui-web';

const fmtDateTime = dateTimeLong; // « 4 août 2026 à 14:32 »
/** Un nombre à la française — UN formateur pour toute la fiche (milliers, tailles). */
const NB = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });
const fmtNb = (n: number) => NB.format(n);
/** Une taille de base lisible : « 412 Mo », « 1,6 Go ». */
const fmtBytes = (b: number) => (b >= 1e9 ? `${fmtNb(b / 1e9)} Go` : `${fmtNb(Math.round(b / 1e6))} Mo`);

/** « expire dans 3 h 12 » — un jeton se lit par le temps qu'il lui reste, pas par sa date de fin. */
function expiresIn(iso: string): string {
  const min = Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60000));
  if (min < 60) return `expire dans ${min} min`;
  return `expire dans ${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`;
}

type Tone = 'ok' | 'wa' | 'da' | 'in' | 'ne' | 'vi';
const Pill = ({ tone, children }: { tone: Tone; children: ReactNode }) => <span className={`sp-pill ${tone}`}>{children}</span>;

/** Un panneau de la fiche : un en-tête (titre, phrase d'usage, actions), puis ses rangées. */
function Panel({ title, note, actions, danger, children }: { title?: string; note?: ReactNode; actions?: ReactNode; danger?: boolean; children?: ReactNode }) {
  return (
    <section className={danger ? 'sp-panel danger' : 'sp-panel'}>
      {title && (
        <header className="sp-ph">
          <div className="t"><h3>{title}</h3>{note && <p>{note}</p>}</div>
          {actions && <div className="acts">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

/** Une rangée « libellé · valeur (· geste) ». */
function Row({ k, children, action, note, mono }: { k: ReactNode; children: ReactNode; action?: ReactNode; note?: ReactNode; mono?: boolean }) {
  return (
    <div className={action ? 'sp-row act' : 'sp-row'}>
      <div className="k">{k}</div>
      <div className={mono ? 'v mono' : 'v'}>{children}{note && <div className="note">{note}</div>}</div>
      {action}
    </div>
  );
}

/** Un interrupteur : un vrai `role="switch"`, annoncé « activé / désactivé ». */
function Toggle({ on, label, onChange, disabled }: { on: boolean; label: string; onChange: (next: boolean) => void; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} className="sp-switch" disabled={disabled} onClick={() => onChange(!on)}>
      <span />
    </button>
  );
}

/** Le texte d'une erreur de lecture, dit à l'endroit où la donnée manque. */
const ReadError = ({ children }: { children: ReactNode }) => (
  <div className="sp-empty" style={{ color: 'var(--da-fg)', overflowWrap: 'anywhere' }}>{children}</div>
);

// ── Carte « Reprises legacy » : la PROVENANCE de la base — qui l'a remplie, depuis où, avec quel résultat.
// Journalisée depuis le 2026-08-23 (migration 0028) : les simulations apparaissent, marquées — c'est une simulation prise
// pour une écriture qui a coûté une tentative. ─────────────────────────────────────────────────────────────────────
/**
 * Date de mise en place du JOURNAL des reprises. Avant elle, une reprise a pu avoir lieu sans laisser de trace : un journal
 * vide ne prouve alors rien.
 * ⚠️ C'est ce qui décide de la phrase du vide. Pour un magasin créé APRÈS cette date, « aucune ligne » veut dire « aucune
 *    reprise » ; pour un magasin antérieur, on le dit avec sa réserve — affirmer une absence qu'on n'a pas constatée
 *    serait faux (§23.0).
 */
const JOURNAL_REPRISES_DEPUIS = Date.parse('2026-08-23T00:00:00Z');

export function RepriseCard({ storeId, storeCreatedAt }: { storeId: string; storeCreatedAt: string | null }) {
  const [runs, setRuns] = useState<Awaited<ReturnType<typeof api.storeImports>>['runs'] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reamorcage, setReamorcage] = useState(false);
  const [redescente, setRedescente] = useState(false);
  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : changer de magasin pendant la lecture afficherait les reprises de l'ANCIEN
  //    sous la fiche du nouveau — et le bouton de relance viserait la base courante sur la foi d'une autre.
  useEffect(() => {
    setRuns(null); setErr(null);
    let alive = true;
    api.storeImports(storeId)
      .then((r) => { if (alive) setRuns(r.runs); })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, [storeId]);
  // L'OUTIL DE RELANCE (Forme G) : l'amorçage est idempotent, mais son échec en fin de reprise ne laissait aucun geste —
  // le seul chemin était une reprise entière. Offert quand la DERNIÈRE écriture le réclame ; le journal des reprises n'est
  // pas réécrit (le seedError reste son histoire). Il renvoie aussi les lignes rejouées que la reprise n'a pas fait
  // descendre — une nouvelle reprise ne les retrouverait plus.
  const derniereEcriture = runs?.find((r) => !r.dryRun);
  const relancer = async () => {
    setReamorcage(true);
    try {
      const r = await api.reseedStoreJournal(storeId);
      showToast(`Amorçage relancé — ${r.seeded} événement(s) journalisé(s)${r.rejournaled > 0 ? `, ${r.rejournaled} ligne(s) rejouée(s) redescendue(s)` : ''}.`);
    } catch (e) {
      showToast(`Amorçage impossible : ${(e as Error).message}`, 'err');
    } finally { setReamorcage(false); }
  };
  // LA RÉPARATION : refaire descendre EN ENTIER ce que la Console écrit en table. Une ligne qu'une reprise rejouée ou une
  // imposition a changée sans la nommer reste à l'ancienne valeur dans le journal — une caisse réinitialisée la
  // relirait telle quelle. ⚠️ Chaque caisse retélécharge ces canaux : la confirmation le dit avant.
  const redescendre = async () => {
    const ok = await confirm({
      title: 'Refaire descendre les fiches aux caisses ?',
      message: 'Articles, clients, titres et référentiels repartent au journal dans leur état actuel. Chaque caisse du magasin les retéléchargera à sa prochaine synchronisation.',
      confirmLabel: 'Refaire descendre',
    });
    if (!ok) return;
    setRedescente(true);
    try {
      const r = await api.reemitStoreChannels(storeId);
      showToast(`Fiches refaites descendre — ${fmtNb(r.emitted)} ligne(s) ré-émise(s).`);
    } catch (e) {
      showToast(`Redescente impossible : ${(e as Error).message}`, 'err');
    } finally { setRedescente(false); }
  };
  const neApresLeJournal = storeCreatedAt != null && Date.parse(storeCreatedAt) >= JOURNAL_REPRISES_DEPUIS;

  return (
    <Panel
      title="Reprise legacy"
      note="La migration depuis l’ancien logiciel du magasin — à ne pas confondre avec la reprise depuis un autre magasin."
      actions={<>
        {derniereEcriture?.seedError && (
          <button type="button" className="btn-ghost sm" disabled={reamorcage} onClick={() => void relancer()}>
            {reamorcage ? 'Amorçage en cours…' : 'Relancer l’amorçage du journal'}
          </button>
        )}
        {derniereEcriture && (
          <button type="button" className="btn-ghost sm" disabled={redescente} onClick={() => void redescendre()}>
            {redescente ? 'Redescente en cours…' : 'Refaire descendre aux caisses'}
          </button>
        )}
      </>}
    >
      {err && <ReadError>{err}</ReadError>}
      {!runs && !err && <div className="sp-empty">Lecture du journal des reprises…</div>}
      {runs && runs.length === 0 && (
        <div className="sp-row wide">
          <div className="v" style={{ fontWeight: 400, color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>
            {neApresLeJournal
              ? 'Aucune reprise legacy : ce magasin a démarré sur une base neuve.'
              // Magasin ANTÉRIEUR au journal : le vide ne prouve rien, il se dit.
              : 'Aucune reprise journalisée. Ce magasin est antérieur à la mise en place du journal (2026-08-23) — une reprise a pu avoir lieu sans y figurer.'}
          </div>
          <Link to="/reprise" className="btn-ghost sm">Ouvrir la reprise legacy</Link>
        </div>
      )}
      {runs && runs.map((r) => (
        <div key={r.id} className="sp-row" style={{ alignItems: 'start' }}>
          <div className="k">
            <Pill tone={r.dryRun ? 'in' : r.ok ? 'ok' : 'da'}>{r.dryRun ? 'SIMULATION' : r.ok ? 'ÉCRITURE' : 'ÉCHEC'}</Pill>
          </div>
          <div className="v" style={{ fontWeight: 400, fontSize: 'var(--t-body)' }}>
            <span className="mono" style={{ color: 'var(--ink-700)' }}>{r.sourceDatabase}</span>
            <span style={{ color: 'var(--ink-400)' }}> @ {r.sourceHost}</span>
            <div className="note">
              {fmtDateTime(r.finishedAt)}{r.createdBy ? ` · ${r.createdBy}` : ''}
              {' · '}{r.dryRun ? `${fmtNb(r.rowsWritten)} à écrire` : `${fmtNb(r.rowsWritten)} écrites`}
              {r.seeded != null ? ` · ${fmtNb(r.seeded)} amorcées` : ''}
              {r.arbitrageStock ? ` · stock : ${r.arbitrageStock === 'cumul' ? 'grand livre' : 'photo'}` : ''}
            </div>
            {/* Les échecs se lisent ICI, pas dans un log serveur : motif réel, jamais résumé. */}
            {r.error && <div className="note" style={{ color: 'var(--da-fg)', overflowWrap: 'anywhere' }}>{r.error}</div>}
            {r.seedError && <div className="note" style={{ color: 'var(--wa-fg)', overflowWrap: 'anywhere' }}>Amorçage du journal en échec : {r.seedError}</div>}
            {r.tablesInError > 0 && <div className="note" style={{ color: 'var(--wa-fg)' }}>{r.tablesInError} table(s) en erreur — voir le rapport de l'import.</div>}
          </div>
        </div>
      ))}
    </Panel>
  );
}

// ── « REPRENDRE DEPUIS UN AUTRE MAGASIN DU CLIENT » ─────────────────────────────────────────────────────
//
// UTILITÉ : à la mise en service d'un nouveau magasin, reprendre le paramétrage d'un magasin du MÊME client plutôt que de
// le refaire à la main. ⚠️ LE GESTE VIT ICI (décision utilisateur) : c'est le staff qui crée les magasins ; le back-office
// copie, la Console demande. ⚠️ UNE COPIE, JAMAIS UN LIEN, et l'APERÇU est obligatoire — une copie écrase ce qu'elle
// recouvre. ⚠️ Les sources sont des POINTS DE VENTE, jamais un dépôt (filtré par le serveur).

/** L'ordre des regroupements, celui de la maquette. Un regroupement inconnu se range à la fin. */
const TAKEOVER_GROUP_ORDER: readonly string[] = ['Comptabilité', 'Catalogue', 'Caisse'];
const groupRank = (g: string) => { const i = TAKEOVER_GROUP_ORDER.indexOf(g); return i < 0 ? TAKEOVER_GROUP_ORDER.length : i; };

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/** Ce qu'une copie a déjà écrit, élément par élément — cumulé sur ses lots. */
type TakeoverTotals = Map<string, { displayLabel: string; takenOver: number; unchanged: number; notes: string[] }>;

/** Une copie en cours, finie ou arrêtée : les éléments demandés, ce qui est écrit, la progression des fiches. */
interface TakeoverRun {
  keys: string[];
  totals: TakeoverTotals;
  lot: { done: number; total: number; next: string | null } | null;
  status: 'running' | 'done' | 'stopped';
  error?: string;
}

/** La carte de reprise de la fiche magasin — exportée pour son banc. */
export function StoreTakeoverCard({ storeId, storeName, storeStatus }: { storeId: string; storeName: string; storeStatus: string }) {
  /** Relie le libellé du magasin source à sa liste. */
  const sourceFieldId = useId();
  const [sources, setSources] = useState<Awaited<ReturnType<typeof api.takeoverSources>>['sources'] | null>(null);
  const [source, setSource] = useState<string>('');
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof api.takeoverPreview>>['items'] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * La copie en cours, ou arrêtée en chemin : ce qui est déjà écrit élément par élément, la progression des fiches, et le
   * curseur où reprendre. ⚠️ GARDÉE APRÈS UN ARRÊT : « Reprendre où la copie s'est arrêtée » repart du dernier lot écrit.
   */
  const [run, setRun] = useState<TakeoverRun | null>(null);

  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : changer de magasin pendant la lecture proposerait comme sources les magasins
  //    voisins de l'ANCIEN — dont, peut-être, le magasin courant lui-même.
  useEffect(() => {
    setSources(null); setSource(''); setPreview(null); setPicked(new Set()); setErr(null); setRun(null);
    if (storeStatus !== 'active') return; // pas de base ici : rien à demander (cf. le message ci-dessous)
    let alive = true;
    api.takeoverSources(storeId)
      .then((r) => { if (alive) setSources(r.sources); })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, [storeId, storeStatus]);

  // ⚠️ L'aperçu est REDEMANDÉ à chaque changement de source : garder l'ancien afficherait ce qu'un AUTRE magasin allait
  //    céder — le pire moment pour se tromper est juste avant d'écraser.
  useEffect(() => {
    if (!source) { setPreview(null); setPicked(new Set()); return; }
    let alive = true;
    setPreview(null); setErr(null);
    api.takeoverPreview(storeId, source)
      .then((r) => { if (alive) { setPreview(r.items); setRun(null); setPicked(new Set(r.items.filter((i) => i.available > 0 && i.preselected).map((i) => i.key))); } })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, [storeId, source]);

  const sourceName = sources?.find((x) => x.id === source)?.name ?? '';
  // ⚠️ LE COMPTE vient de `overwrittenCount`, les NOMS de `overwritten` : un catalogue en remplace des milliers, et l'aperçu
  //    n'en nomme qu'un échantillon.
  const pickedItems = (preview ?? []).filter((i) => picked.has(i.key));
  const overwritten = pickedItems.flatMap((i) => i.overwritten);
  const overwrittenCount = pickedItems.reduce((n, i) => n + i.overwrittenCount, 0);
  // ⚠️ LE REGISTRE DU BACK-OFFICE EST LA SEULE LISTE de ce qui se reprend : l'écran n'en tient aucune à lui. Pas de
  //    fichier clients (décision utilisateur) : la reprise sert la mise en service, le pied le range dans « Jamais repris ».
  const groups = [...new Set((preview ?? []).map((i) => i.group))]
    .sort((a, b) => groupRank(a) - groupRank(b));

  /**
   * Enchaîne les appels d'une copie : le premier écrit les éléments simples et le premier lot de fiches, les suivants les
   * lots restants, avec le curseur que chaque rapport rend.
   * ⚠️ UN ARRÊT NE PERD RIEN : ce qui est écrit le reste, le curseur est gardé, et « Reprendre où la copie s'est arrêtée »
   *    repart de là — relancer depuis le début ne réécrirait d'ailleurs que ce qui diffère.
   */
  const drive = async (start: TakeoverRun, after: string | null) => {
    const totals: TakeoverTotals = new Map(start.totals);
    let lot = start.lot;
    let cursor = after;
    setBusy(true);
    setRun({ ...start, status: 'running' });
    try {
      for (;;) {
        const r = await api.takeoverApply(storeId, source, start.keys, cursor);
        for (const x of r.items) {
          const before = totals.get(x.key) ?? { displayLabel: x.displayLabel, takenOver: 0, unchanged: 0, notes: [] };
          totals.set(x.key, {
            displayLabel: x.displayLabel,
            takenOver: before.takenOver + x.takenOver,
            unchanged: before.unchanged + x.unchanged,
            notes: [...before.notes, ...x.notes.filter((n) => !before.notes.includes(n))],
          });
        }
        lot = r.lot ? { done: (lot?.done ?? 0) + r.lot.scanned, total: r.lot.total, next: r.lot.next } : null;
        cursor = r.lot?.next ?? null;
        setRun({ keys: start.keys, totals: new Map(totals), lot, status: cursor ? 'running' : 'done' });
        if (!cursor) break;
      }
      // Le rapport dit ce qui a RÉELLEMENT été écrit, élément par élément — et ce qui était déjà identique, qui n'a pas été
      // réécrit. Jamais un « c'est fait » global.
      showToast(`Repris — ${[...totals.values()].map((x) => `${x.displayLabel} : ${x.takenOver}${x.unchanged ? ` (${plural(x.unchanged, 'déjà identique', 'déjà identiques')})` : ''}${x.notes.length ? ` — ${x.notes.join(' ')}` : ''}`).join(' · ')}`);
      const again = await api.takeoverPreview(storeId, source);
      setPreview(again.items);
    } catch (e) {
      // ⚠️ L'ARRÊT SE DIT avec son motif réel, et la copie garde ce qu'elle a écrit et son curseur.
      setRun({ keys: start.keys, totals: new Map(totals), lot: lot ? { ...lot, next: cursor } : null, status: 'stopped', error: (e as Error).message });
      showToast(`Reprise arrêtée : ${(e as Error).message}`, 'err');
    } finally { setBusy(false); }
  };

  const apply = async () => {
    const keys = [...picked];
    if (keys.length === 0) return;
    const ok = await confirm({
      title: `Reprendre le paramétrage de « ${sourceName} » ?`,
      message: overwrittenCount > 0
        ? `${plural(overwrittenCount, 'élément déjà présent ici sera REMPLACÉ', 'éléments déjà présents ici seront REMPLACÉS')} : ${overwritten.slice(0, 6).join(', ')}${overwrittenCount > 6 ? '…' : ''}. La copie est définitive — ce qui est repris devient la propriété de ce magasin.`
        : 'Le paramétrage est copié dans ce magasin. C’est une copie, pas un lien : le corriger ici ne touchera pas le magasin source.',
      confirmLabel: 'Reprendre',
      ...(overwrittenCount > 0 ? { tone: 'danger' as const } : {}),
    });
    if (!ok) return;
    await drive({ keys, totals: new Map(), lot: null, status: 'running' }, null);
  };

  /** Reprend une copie arrêtée en chemin, au curseur gardé. */
  const continueRun = async () => {
    if (!run?.lot?.next) return;
    await drive(run, run.lot.next);
  };

  return (
    <Panel
      title="Reprendre depuis un autre magasin du client"
      note="Une copie datée et sourcée, jamais un lien : ce qui est repris appartient ensuite à ce magasin. Rien n’est écrasé sans l’avoir vu dans l’aperçu."
    >
      {/* ⚠️ CIBLE PAS ENCORE EN SERVICE = pas de base ici : l'écran DIT l'ordre des gestes au lieu de proposer une reprise
          qui échouerait en « base injoignable » (§23.0). */}
      {storeStatus !== 'active' ? (
        <div className="sp-empty">Ce magasin n’a pas encore de base : <b>mettez-le en service</b>, puis revenez reprendre le paramétrage d’un autre magasin.</div>
      ) : (<>
        {err && <ReadError>{err}</ReadError>}
        {!sources && !err && <div className="sp-empty">Lecture des magasins du client…</div>}
        {/* Aucun autre POINT DE VENTE en service : on le DIT, plutôt qu'un sélecteur vide qu'on croirait cassé. */}
        {sources && sources.length === 0 && (
          <div className="sp-empty">Ce client n’a aucun autre point de vente en service : rien à reprendre pour l’instant. La reprise devient possible dès qu’un second magasin est mis en service.</div>
        )}
        {sources && sources.length > 0 && (
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--ink-100)' }}>
            <label htmlFor={sourceFieldId} style={{ display: 'block', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-600)', marginBottom: 6 }}>Magasin source</label>
            <select id={sourceFieldId} value={source} onChange={(e) => setSource(e.target.value)} disabled={busy}
              style={{ width: '100%', maxWidth: 420, height: 36, padding: '0 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)', background: 'var(--canvas)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)' }}>
              <option value="">— choisir un magasin —</option>
              {sources.map((x) => <option key={x.id} value={x.id}>{x.name}{x.sCode ? ` (S${x.sCode})` : ''}</option>)}
            </select>
          </div>
        )}
        {source && !preview && !err && <div className="sp-empty">Lecture de ce qui peut être repris…</div>}
        {sources && sources.length > 0 && !source && <div className="sp-empty">Choisissez le magasin dont vous voulez reprendre le paramétrage : l’aperçu s’affiche ici.</div>}
        {preview && groups.map((g) => (
          <div key={g} data-takeover-group={g}>
            <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink-500)', padding: '12px 18px 4px' }}>{g}</div>
            {preview.filter((i) => i.group === g).map((i) => {
              // ⚠️ Un élément SANS RIEN À CÉDER n'est pas cochable : l'offrir laisserait croire qu'on va reprendre quelque
              //    chose, et le rapport final dirait « 0 » sans qu'on comprenne.
              const empty = i.available === 0;
              return (
                <label key={i.key} data-takeover-item={i.key} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 18px', opacity: empty ? 0.55 : 1 }}>
                  <input type="checkbox" disabled={empty || busy} checked={picked.has(i.key)}
                    onChange={(e) => setPicked((c) => { const n = new Set(c); if (e.target.checked) n.add(i.key); else n.delete(i.key); return n; })}
                    style={{ marginTop: 3 }} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600 }}>{i.displayLabel}</span>
                      <Pill tone="in">{i.available} à reprendre</Pill>
                      {i.overwrittenCount > 0 && <Pill tone="wa">{plural(i.overwrittenCount, 'remplacé', 'remplacés')}</Pill>}
                    </span>
                    <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 3, lineHeight: 1.5 }}>{i.description}</span>
                    {/* Ce que la copie ne fera PAS se dit ici, avant de cocher (§23.0). */}
                    {i.notes.map((n) => <span key={n} data-takeover-note style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-600)', marginTop: 3, lineHeight: 1.5 }}>{n}</span>)}
                    {/* ⚠️ Les écrasés sont NOMMÉS, jamais seulement comptés : c'est ce qui fait décider de cocher. */}
                    {i.overwrittenCount > 0 && (
                      <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--wa-fg)', marginTop: 3, lineHeight: 1.5, overflowWrap: 'anywhere' }}>Sera remplacé ici : {i.overwritten.join(', ')}{i.overwrittenCount > i.overwritten.length ? ` et ${plural(i.overwrittenCount - i.overwritten.length, 'autre', 'autres')}` : ''}</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        ))}
        <div className="sp-foot"><b style={{ color: 'var(--ink-700)' }}>Jamais repris</b> : le stock, les ventes et pièces fiscales, les compteurs, le fichier clients, les points de fidélité, les titres en circulation, les appareils et leurs périphériques.</div>
        {run && (
          <div data-takeover-run style={{ padding: '12px 18px', borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-dense)', lineHeight: 1.5 }}>
            {run.lot && <div>Fiches parcourues : {run.lot.done} / {run.lot.total}{run.status === 'running' ? ' — gardez cette page ouverte jusqu’à la fin.' : ''}</div>}
            {run.status === 'stopped' && (
              <div style={{ color: 'var(--da-fg)', marginTop: 4, overflowWrap: 'anywhere' }}>
                La copie s’est arrêtée : {run.error}{run.lot?.next ? ' Ce qui précède est écrit ; elle peut reprendre où elle s’est arrêtée.' : ''}
              </div>
            )}
            {run.status === 'stopped' && run.lot?.next && (
              <button type="button" className="btn-ghost sm" style={{ marginTop: 8 }} disabled={busy} onClick={() => void continueRun()}>Reprendre où la copie s’est arrêtée</button>
            )}
          </div>
        )}
        {preview && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 18px', borderTop: '1px solid var(--ink-200)', background: 'var(--panel)' }}>
            <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
              {picked.size === 0 ? 'Cochez au moins un élément.' : `${plural(picked.size, 'élément', 'éléments')} depuis « ${sourceName} »${overwrittenCount ? ` · ${plural(overwrittenCount, 'élément remplacé', 'éléments remplacés')}` : ''}`}
            </span>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn-cta" disabled={busy || picked.size === 0} onClick={() => void apply()}>
              {busy ? 'Reprise en cours…' : `Reprendre dans « ${storeName} »`}
            </button>
          </div>
        )}
      </>)}
    </Panel>
  );
}

// ── « Compte SMS » (§11.1) : à quel compte d'envoi CE magasin est rattaché, et s'il peut réellement envoyer.
// ⚠️ RATTACHÉ ≠ CAPABLE : l'opérateur débite le compte AUTHENTIFIÉ, et sa liste ne donne pas de mots de passe — un compte
// pris dans sa liste est connu et MUET tant que personne n'a saisi son accès (§23.0).
// ⚠️ L'ÉTAT SE LIT SANS APPELER L'OPÉRATEUR (nos comptes enregistrés) : c'est le bouton « Tester » qui l'interroge.
// ⚠️ AUCUN PRIX : nos tarifs ne sont pas les siens. Ce qui se compte ici, ce sont des SMS.
function CompteSmsCard({ gln, storeName }: { gln: string; storeName: string }) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const [compte, setCompte] = useState<BoMessagingAccount | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [chargé, setChargé] = useState(false);
  const [probe, setProbe] = useState<BoStoreAccountProbe | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'vue' | 'rattacher' | 'creer'>('vue');

  // Le choix d'un sous-compte existant — la liste vient de l'opérateur, à la demande.
  const [liste, setListe] = useState<Awaited<ReturnType<typeof api.boSubaccounts>> | null>(null);
  const [choisi, setChoisi] = useState<string>('');
  const [motDePasse, setMotDePasse] = useState('');
  // La création — l'identifiant du sous-compte chez l'opérateur.
  const [login, setLogin] = useState('');
  const [libelle, setLibelle] = useState('');

  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : changer de magasin pendant la lecture afficherait le compte SMS de l'ANCIEN ;
  //    et la relecture qui suit un rattachement croise celle de l'ouverture — revenue la dernière, l'ancienne annoncerait
  //    « aucun compte » sur un magasin qu'on vient de rattacher.
  const latest = useLatest();
  const load = useCallback(() => {
    const isLatest = latest();
    return api.boMessagingAccounts()
      .then((r) => {
        if (!isLatest()) return;
        setCompte(r.accounts?.find((a) => a.stores.some((x) => x.gln === gln)) ?? null);
        setErr(r.ok ? null : (r.error ?? 'Comptes illisibles.'));
        setChargé(true);
      })
      .catch((e) => { if (!isLatest()) return; setCompte(null); setErr((e as Error).message); setChargé(true); });
  }, [gln, latest]);
  useEffect(() => { setChargé(false); setProbe(null); setMode('vue'); void load(); }, [load]);

  const ouvrirRattachement = async () => {
    setMode('rattacher'); setChoisi(''); setMotDePasse('');
    if (liste) return;
    setBusy(true);
    try { setListe(await api.boSubaccounts()); }
    catch (e) { await alert({ title: 'Liste indisponible', message: (e as Error).message, tone: 'err' }); setMode('vue'); }
    finally { setBusy(false); }
  };

  const rattacher = async () => {
    const sub = liste?.subaccounts?.find((x) => x.externalId === choisi);
    if (!sub?.externalId) return;
    setBusy(true);
    try {
      const r = await api.attachBoMessagingAccount({
        gln, externalId: sub.externalId,
        ...(sub.login ? { login: sub.login } : {}),
        ...(sub.label ? { label: sub.label } : {}),
        ...(motDePasse ? { password: motDePasse } : {}),
      });
      if (!r.ok) throw new Error(r.error ?? 'Rattachement refusé.');
      // ⚠️ L'ISSUE VIENT DE L'ÉPREUVE rendue par le serveur, jamais présumée ici : c'est elle qui distingue « rattaché »
      //    de « prêt à envoyer ».
      setProbe(r.probe ?? null);
      showToast(r.probe?.canSend
        ? `Compte rattaché à ${storeName} — il peut envoyer.`
        : `Compte rattaché à ${storeName}, mais MUET : ${r.probe?.sendError ?? 'aucun mot de passe détenu'}.`);
      setMode('vue');
      // ⚠️ LA LISTE DE L'OPÉRATEUR EST PÉRIMÉE : ses mentions « déjà utilisé par … » viennent de croiser les rattachements,
      //    qu'on vient de changer.
      setListe(null);
      await load();
    } catch (e) { await alert({ title: 'Rattachement impossible', message: (e as Error).message, tone: 'err' }); }
    finally { setBusy(false); }
  };

  const creer = async () => {
    if (!login.trim()) return;
    setBusy(true);
    try {
      const r = await api.createBoSubaccount({ login: login.trim(), label: libelle.trim() || login.trim(), gln });
      if (!r.ok) {
        // ⚠️ L'ORPHELIN A SON PROPRE MESSAGE : le compte EXISTE chez l'opérateur (et sera facturé). Un « échec » sec ferait
        //    recréer un doublon que plus personne ne saurait rattacher.
        throw new Error(r.orphan
          ? `Le compte ${r.orphan.externalId} a bien été créé chez l'opérateur, mais n'a pas pu être enregistré ici (${r.orphan.reason}). Mot de passe à conserver : ${r.orphan.password}`
          : (r.error ?? 'Création refusée.'));
      }
      // ⚠️ LE MOT DE PASSE NE RESSORTIRA PLUS JAMAIS : c'est le seul instant où il quitte le serveur.
      await alert({
        title: 'Sous-compte créé',
        message: `Identifiant ${r.login} (${r.externalId}).\nMot de passe — noté maintenant ou perdu : ${r.password}\n\n${r.attached ? 'Rattaché à ce magasin.' : `NON rattaché : ${r.attachError ?? 'raison inconnue'}`}`,
        // ⚠️ `info` et non un ton d'échec : la création a RÉUSSI, et ce cartouche porte un mot de passe qui ne ressortira
        //    plus — le teinter en erreur ferait fermer sans lire.
        tone: 'info',
      });
      setMode('vue'); setLogin(''); setLibelle(''); setListe(null);
      await load();
    } catch (e) { await alert({ title: 'Création impossible', message: (e as Error).message, tone: 'err' }); }
    finally { setBusy(false); }
  };

  const tester = async () => {
    setBusy(true);
    try {
      const r = await api.testBoStoreAccount(gln);
      setProbe(r.probe ?? null);
      if (!r.probe) await alert({ title: 'Test impossible', message: r.error ?? 'Le back-office n’a rien rendu.', tone: 'err' });
    } catch (e) { await alert({ title: 'Test impossible', message: (e as Error).message, tone: 'err' }); }
    finally { setBusy(false); }
  };

  const detacher = async () => {
    if (!await confirm({
      title: 'Détacher le compte SMS ?',
      message: `${storeName} ne pourra plus envoyer de SMS. Le compte, lui, reste chez l’opérateur avec ses crédits — détacher n’est pas supprimer.`,
      // Ton `danger` : le magasin perd sa capacité d'envoi — c'est une privation, pas un réglage.
      confirmLabel: 'Détacher', tone: 'danger',
    })) return;
    setBusy(true);
    try {
      const r = await api.detachBoMessagingAccount(gln);
      if (!r.ok) throw new Error(r.error ?? 'Détachement refusé.');
      showToast(r.detached ? 'Compte détaché.' : 'Aucun rattachement à retirer.');
      setProbe(null);
      setListe(null); // même raison qu'au rattachement : les magasins croisés ont changé.
      await load();
    } catch (e) { await alert({ title: 'Détachement impossible', message: (e as Error).message, tone: 'err' }); }
    finally { setBusy(false); }
  };

  const inputSt: CSSProperties = { width: '100%', height: 32, padding: '0 10px', borderRadius: 'var(--r-xs)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-body)' };
  const noteSt: CSSProperties = { fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 6, lineHeight: 1.5, fontWeight: 400 };
  const partage = compte && compte.stores.length > 1;

  return (
    <div className="sp-row act" style={{ alignItems: 'start' }}>
      <div className="k" style={{ paddingTop: 2 }}>Compte SMS</div>
      <div className="v" style={{ fontWeight: 400 }}>
        {err && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--da-fg)', lineHeight: 1.5, overflowWrap: 'anywhere' }}>{err}</div>}
        {!chargé && !err && <div style={noteSt}>Lecture des comptes déclarés…</div>}

        {chargé && !compte && mode === 'vue' && (
          <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>Aucun compte rattaché : ce magasin n’envoie pas de SMS tant qu’un sous-compte ne lui est pas rattaché.</div>
        )}

        {chargé && compte && mode === 'vue' && (
          <>
            <div style={{ fontSize: 'var(--t-lead)', fontWeight: 600 }}><span className="mono">{compte.label}</span></div>
            <div style={{ ...noteSt, marginTop: 2 }}>identifiant opérateur <code>{compte.externalId}</code> · login <code>{compte.login}</code></div>
            {!compte.hasSecret && (
              // ⚠️ DIT, pas caché : rattaché sans mot de passe, ce compte ne fera partir aucun SMS.
              <div style={{ ...noteSt, color: 'var(--wa-fg)' }}>
                Aucun mot de passe n’est détenu pour ce sous-compte : il est rattaché, mais <b>rien ne peut partir</b>. Rattachez-le de nouveau en saisissant son mot de passe.
              </div>
            )}
            {compte.hasSecret && !probe && (
              // ⚠️ L'ENTRE-DEUX SE DIT AUSSI : détenir un mot de passe n'est pas un envoi qui marche — seul « Tester » tranche.
              <div style={noteSt}>Un mot de passe est détenu, <b>non éprouvé</b> depuis l’ouverture de cette fiche. « Tester » le vérifie auprès de l’opérateur, sans envoyer de SMS.</div>
            )}
            {partage && (
              // ⚠️ Le partage se DIT avant d'être subi : ces boutiques puisent dans la MÊME réserve.
              <div style={{ ...noteSt, color: 'var(--wa-fg)' }}>
                Compte <b>partagé</b> avec {compte.stores.filter((x) => x.gln !== gln).map((x) => x.name ?? x.gln).join(', ')} — ces magasins puisent dans la même réserve de SMS.
              </div>
            )}
            {probe && (
              <div style={{ ...noteSt, color: 'var(--ink-700)' }}>
                {probe.credits
                  ? (probe.credits.length
                      ? <>Crédits chez l’opérateur : {probe.credits.map((c) => `${c.quantity} SMS (${c.type})`).join(' · ')}</>
                      : <>Aucun crédit sur ce sous-compte.</>)
                  : <span style={{ color: 'var(--da-fg)' }}>Crédits illisibles : {probe.creditsError}</span>}
                {probe.sendError && <div style={{ color: 'var(--da-fg)', marginTop: 4 }}>{probe.sendError}</div>}
              </div>
            )}
          </>
        )}

        {mode === 'rattacher' && (
          <div>
            {busy && !liste && <div style={noteSt}>Interrogation de l’opérateur…</div>}
            {liste && !liste.ok && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--da-fg)', lineHeight: 1.5 }}>{liste.error}</div>}
            {liste?.ok && (
              <>
                <div id={`${fieldId}-0`} style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginBottom: 4 }}>Sous-compte de notre compte maître</div>
                <select aria-labelledby={`${fieldId}-0`} value={choisi} onChange={(e) => setChoisi(e.target.value)} style={inputSt}>
                  <option value="">— choisir —</option>
                  {liste.subaccounts?.filter((x) => x.externalId).map((x) => (
                    <option key={x.externalId!} value={x.externalId!}>
                      {x.label ?? x.login ?? x.externalId}
                      {x.stores.length ? ` — déjà utilisé par ${x.stores.map((st) => st.name ?? st.gln).join(', ')}` : ''}
                    </option>
                  ))}
                </select>
                {/* ⚠️ Un sous-compte que l'opérateur décrit sans identifiant n'est PAS rattachable : on le compte plutôt
                    que de l'offrir dans une liste où il échouerait au clic. */}
                {!!liste.unidentified && (
                  <div style={noteSt}>{liste.unidentified} sous-compte(s) rendus par l’opérateur sans identifiant exploitable — non rattachables.</div>
                )}
                <div id={`${fieldId}-1`} style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', margin: '10px 0 4px' }}>Mot de passe du sous-compte (facultatif)</div>
                <input aria-labelledby={`${fieldId}-1`} type="password" value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} style={inputSt} autoComplete="new-password" />
                <div style={noteSt}>Sans lui, le compte est rattaché mais <b>muet</b> : l’opérateur débite le compte authentifié, et nous ne saurions pas nous authentifier comme celui-ci.</div>
              </>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button className="btn-ghost sm" onClick={() => setMode('vue')} disabled={busy}>Annuler</button>
              <button className="btn-cta sm" onClick={() => void rattacher()} disabled={busy || !choisi}>{busy ? '…' : 'Rattacher'}</button>
            </div>
          </div>
        )}

        {mode === 'creer' && (
          <div>
            <div id={`${fieldId}-2`} style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginBottom: 4 }}>Identifiant du sous-compte (login opérateur)</div>
            <input aria-labelledby={`${fieldId}-2`} value={login} onChange={(e) => setLogin(e.target.value)} style={inputSt} autoComplete="off" />
            <div id={`${fieldId}-3`} style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', margin: '10px 0 4px' }}>Libellé (pour nous)</div>
            <input aria-labelledby={`${fieldId}-3`} value={libelle} onChange={(e) => setLibelle(e.target.value)} style={inputSt} placeholder={storeName} autoComplete="off" />
            <div style={noteSt}>
              Le mot de passe est engendré par le back-office et affiché <b>une seule fois</b> — ensuite il ne ressort plus. Le compte est créé chez l’opérateur et rattaché à ce magasin dans la foulée.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button className="btn-ghost sm" onClick={() => setMode('vue')} disabled={busy}>Annuler</button>
              <button className="btn-cta sm" onClick={() => void creer()} disabled={busy || !login.trim()}>{busy ? '…' : 'Créer le compte'}</button>
            </div>
          </div>
        )}
      </div>
      {mode === 'vue' && chargé && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {compte ? (
            <>
              {/* ⚠️ TROIS ÉTATS, PAS DEUX : « peut envoyer » ne s'écrit qu'après une épreuve réussie (§23.0). */}
              <Pill tone={probe?.canSend ? 'ok' : compte.hasSecret ? 'in' : 'wa'}>{probe?.canSend ? 'peut envoyer' : compte.hasSecret ? 'à éprouver' : 'muet'}</Pill>
              <button className="btn-ghost sm" onClick={() => void tester()} disabled={busy}>{busy ? '…' : 'Tester'}</button>
              <button className="btn-ghost sm" onClick={() => void ouvrirRattachement()} disabled={busy}>Changer</button>
              <button className="btn-ghost sm danger" onClick={() => void detacher()} disabled={busy}>Détacher</button>
            </>
          ) : (
            <>
              <button className="btn-ghost sm" onClick={() => void ouvrirRattachement()} disabled={busy}>Rattacher</button>
              <button className="btn-ghost sm" onClick={() => { setMode('creer'); setLogin(''); setLibelle(''); }} disabled={busy}>Créer un compte</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── « Synchro périodique obligatoire » (§13.1quater) : état RÉEL lu dans la base du magasin, édition des délais
// (allowlist serveur). Réglages absents = règle inerte (magasin mis en service avant la fonctionnalité) — dit tel quel. ─
function PeriodicPanel({ storeId, vals, onSaved }: { storeId: string; vals: Record<string, string>; onSaved: () => void }) {
  const warnId = useId();
  const lockId = useId();
  const hasAny = 'api.offline_rule' in vals;
  // ⚠️ LA LECTURE DE LA CAISSE, prise au contrat : absente, la règle est inerte — et le panneau le dit.
  const savedOn = hasAny && switchOn('api.offline_rule', vals['api.offline_rule']);
  const days = (k: string, fallback: string) => (vals[k] && vals[k] !== '0' ? vals[k]! : fallback);
  const savedWarn = days('api.offline_warn_days', OFFLINE_RULE_DEFAULTS.warnDays);
  const savedLock = days('api.offline_lock_days', OFFLINE_RULE_DEFAULTS.lockDays);
  const [on, setOn] = useState(savedOn);
  const [warn, setWarn] = useState(savedWarn);
  const [lock, setLock] = useState(savedLock);
  const [busy, setBusy] = useState(false);
  // Relue après un enregistrement : le formulaire repart de ce que la base porte VRAIMENT.
  useEffect(() => { setOn(savedOn); setWarn(savedWarn); setLock(savedLock); }, [savedOn, savedWarn, savedLock]);
  const dirty = on !== savedOn || (on && (warn !== savedWarn || lock !== savedLock));
  const err = offlineRuleError(on, warn, lock);

  const save = async () => {
    if (err) return;
    setBusy(true);
    try {
      const r = await api.updateStoreSettings(storeId, {
        'api.offline_rule': on ? '1' : '0',
        'api.offline_warn_days': on ? warn : '0',
        'api.offline_lock_days': on ? lock : '0',
      });
      // ⚠️ L'issue de la DESCENTE vient du serveur (journalisation réelle), jamais présumée ici.
      showToast(`Synchro périodique enregistrée — ${r.descend.note}.`);
      onSaved();
    // ⚠️ ALERTE, PAS TOAST (§24.1quater) : une erreur qui s'efface toute seule laisserait croire les délais enregistrés.
    } catch (e) { await alert({ title: 'Enregistrement impossible', message: (e as Error).message, tone: 'err' }); }
    finally { setBusy(false); }
  };

  const note = err ?? (on
    ? `Avertissement à J+${warn} sans synchronisation, blocage à J+${lock}. Descend aux caisses à leur prochain contact.`
    : hasAny
      ? 'Désactivée (profil itinérant) : une caisse peut rester sans synchronisation indéfiniment.'
      : 'Magasin mis en service avant la règle : elle est inerte tant que ses délais ne sont pas posés.');
  return (
    <Panel
      title="Synchro périodique obligatoire"
      note="Une caisse qui ne se synchronise plus est d’abord avertie, puis bloquée ; une synchronisation réussie la débloque seule. Les délais se comptent sans aucune synchronisation."
      actions={<Toggle on={on} label="Synchro périodique obligatoire" onChange={setOn} disabled={busy} />}
    >
      <div style={{ opacity: on ? 1 : 0.45, pointerEvents: on ? undefined : 'none', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '14px 18px', padding: '14px 18px' }}>
        <div>
          <label htmlFor={warnId} style={{ display: 'block', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-600)', marginBottom: 6 }}>Avertissement en caisse après</label>
          <span className="sp-field">
            <input id={warnId} value={warn} onChange={(e) => setWarn(e.target.value.replace(/\D/g, '').slice(0, 3))} inputMode="numeric" className={err ? 'sp-input bad' : 'sp-input'} disabled={!on} />
            <span className="sp-unit">jours</span>
          </span>
        </div>
        <div>
          <label htmlFor={lockId} style={{ display: 'block', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-600)', marginBottom: 6 }}>Blocage automatique après</label>
          <span className="sp-field">
            <input id={lockId} value={lock} onChange={(e) => setLock(e.target.value.replace(/\D/g, '').slice(0, 3))} inputMode="numeric" className={err ? 'sp-input bad' : 'sp-input'} disabled={!on} />
            <span className="sp-unit">jours</span>
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '11px 18px', borderTop: '1px solid var(--ink-100)', background: 'var(--panel)' }}>
        <span role={err ? 'alert' : undefined} style={{ fontSize: 'var(--t-note)', lineHeight: 1.45, color: err ? 'var(--da-fg)' : 'var(--ink-500)' }}>{note}</span>
        <div style={{ flex: 1 }} />
        {dirty && (
          <>
            <button className="btn-ghost sm" disabled={busy} onClick={() => { setOn(savedOn); setWarn(savedWarn); setLock(savedLock); }}>Annuler</button>
            <button className="btn-cta sm" disabled={busy || !!err} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
          </>
        )}
      </div>
    </Panel>
  );
}

/** « 1,2,3,4,5,6 » → « lun · mar · mer · jeu · ven · sam » (1 = lundi … 7 = dimanche, la convention du back-office). */
const WEEKDAYS = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];
const openDaysLabel = (v: string | undefined) => {
  const days = (v ?? '').split(',').map((x) => Number.parseInt(x, 10)).filter((n) => n >= 1 && n <= 7).sort((a, b) => a - b);
  return days.length ? days.map((n) => WEEKDAYS[n - 1]).join(' · ') : null;
};

/** Une validité de titre telle que la caisse la stocke (`{ val, unit, text }` en JSON) — `null` si absente ou illisible. */
function readValidity(v: string | undefined): TitleValidity | null {
  if (!v) return null;
  try {
    const o = JSON.parse(v) as Partial<TitleValidity>;
    return typeof o.val === 'number' && typeof o.unit === 'string' ? { val: o.val, unit: o.unit, text: o.text ?? '' } : null;
  } catch { return null; }
}

// ════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
export function StoreDetail({ clientId, clientName, tenantTrade, store: s, stores, devices, onBackClients, onBackClient, onProvision, provBusy, onChanged }: {
  /** Le client du magasin — ses blocages d'abonnement touchent aussi ce magasin. */
  clientId: string;
  clientName: string;
  /** Métier par défaut du CLIENT (provenance « hérité du client » du type de commerce). */
  tenantTrade?: string | null;
  store: TenantStore;
  /** Tous les magasins du client — candidats de la liste d'accès d'un Mobile Partner. */
  stores: TenantStore[];
  devices: TenantDevice[];                    // appareils du CLIENT — filtrés ici sur ce magasin
  onBackClients: () => void;
  onBackClient: () => void;
  onProvision: () => void;
  provBusy: boolean;
  /** « La fiche a bougé, relis-la » — rien d'autre ; ce qui se dit passe par le toast. */
  onChanged: () => void;
}) {
  const nav = useNavigate();
  const { user } = useAuth();
  // Blocage d'une CAISSE/d'un magasin : délégué à deploy_ops (super-admin inclus). On n'offre pas un bouton que le serveur
  // refusera — l'écran ne promet que ce qu'il peut tenir (§23.0).
  const canBlockStore = can(user, 'console.killswitch.store');
  // Entrer dans le back-office d'un CLIENT : verbe réservé au super-admin (SA-7). Même règle.
  const canCrossTenant = can(user, 'console.explorer.write');
  const active = s.status === 'active';
  const depot = s.kind === 'depot';
  const dbName = `mag_${s.gln.replace(/\D/g, '')}`;
  const mine = useMemo(() => devices.filter((d) => d.storeId === s.id && d.status !== 'revoked'), [devices, s.id]);
  const tills = useMemo(() => mine.filter((d) => d.registerNumber != null).sort((a, b) => (a.registerNumber ?? 0) - (b.registerNumber ?? 0)), [mine]);

  // ── La section ouverte : la vue d'ensemble à chaque nouveau magasin. ──
  const [section, setSection] = useState<StoreSection>('overview');
  useEffect(() => { setSection('overview'); }, [s.id]);
  // ⚠️ LES SECTIONS SONT DES ONGLETS, AU CLAVIER AUSSI : la section choisie seule entre dans la tabulation, les flèches
  //    (haut/bas comme gauche/droite — la liste est verticale à trois colonnes, en grille à une) choisissent la voisine.
  const tabsId = useId();
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const target = rovingTarget(e.key, i, STORE_SECTIONS.length, undefined, 'both');
    if (target === null) return;
    e.preventDefault();
    setSection(STORE_SECTIONS[target]!.key);
    e.currentTarget.closest('[role="tablist"]')?.querySelectorAll<HTMLElement>('[role="tab"]')[target]?.focus();
  };

  // ── La disposition suit la largeur RÉELLE de la page (et non de la fenêtre : le menu de la Console prend sa place). ──
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<StoreLayout>('three');
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setLayout(layoutForWidth(el.clientWidth)));
    ro.observe(el);
    setLayout(layoutForWidth(el.clientWidth));
    return () => ro.disconnect();
  }, []);

  // ── Les lectures de la page. Chacune dit son échec à l'endroit où sa donnée manque — jamais un zéro. ──
  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : chaque lecture est liée au magasin affiché ; sans ce garde, les chiffres de
  //    l'ancien magasin s'afficheraient sous la fiche du nouveau.
  const [blocks, setBlocks] = useState<{ active: BlockRow[]; history: BlockRow[] } | null>(null);
  const [blocksErr, setBlocksErr] = useState<string | null>(null);
  const loadBlocks = useCallback(() => {
    api.blocks().then((r) => { setBlocks(r); setBlocksErr(null); }).catch((e) => { setBlocks(null); setBlocksErr((e as Error).message); });
  }, []);
  useEffect(() => { loadBlocks(); }, [loadBlocks]);

  const [sup, setSup] = useState<{ store: SupervisionStore | null } | null>(null);
  const [supErr, setSupErr] = useState<string | null>(null);
  useEffect(() => {
    setSup(null); setSupErr(null);
    let alive = true;
    api.supervision()
      .then((r) => { if (alive) setSup({ store: r.stores.find((x) => x.gln === s.gln) ?? null }); })
      .catch((e) => { if (alive) setSupErr((e as Error).message); });
    return () => { alive = false; };
  }, [s.gln]);

  // Le RELEVÉ DU STORE : la version publique de CHAQUE application (`/v1/store-apps`), et non plus de la seule caisse.
  const [storeApps, setStoreApps] = useState<StoreAppsSurvey | null>(null);
  const [storeAppsErr, setStoreAppsErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api.storeApps()
      .then((r) => { if (alive) setStoreApps(r); })
      .catch((e) => { if (alive) setStoreAppsErr((e as Error).message); });
    return () => { alive = false; };
  }, []);

  const [counters, setCounters] = useState<Awaited<ReturnType<typeof api.storeCounters>> | null>(null);
  const [countersErr, setCountersErr] = useState<string | null>(null);
  useEffect(() => {
    setCounters(null); setCountersErr(null);
    if (!active) return;
    let alive = true;
    api.storeCounters(s.id)
      .then((d) => { if (alive) setCounters(d); })
      .catch((e) => { if (alive) setCountersErr((e as Error).message); });
    return () => { alive = false; };
  }, [s.id, active]);

  const [settings, setSettings] = useState<Record<string, string> | null>(null);
  const [settingsErr, setSettingsErr] = useState<string | null>(null);
  const latestSettings = useLatest();
  const loadSettings = useCallback(() => {
    if (!active) { setSettings(null); setSettingsErr(null); return; }
    const isLatest = latestSettings();
    api.storeSettings(s.id)
      .then((r) => { if (isLatest()) { setSettings(r.settings); setSettingsErr(null); } })
      .catch((e) => { if (isLatest()) { setSettings(null); setSettingsErr((e as Error).message); } });
  }, [s.id, active, latestSettings]);
  useEffect(() => { setSettings(null); setSettingsErr(null); loadSettings(); }, [loadSettings]);

  // Version de schéma RÉELLE de la base (relue par /migrations/status) — jamais estimée.
  const [mig, setMig] = useState<MigrationsStatus | null>(null);
  const [migErr, setMigErr] = useState<string | null>(null);
  useEffect(() => {
    setMig(null); setMigErr(null);
    if (!active) return;
    let alive = true;
    api.migrationsStatus().then((m) => { if (alive) setMig(m); }).catch((e) => { if (alive) setMigErr((e as Error).message); });
    return () => { alive = false; };
  }, [s.id, active]);
  const migRow = mig?.stores.find((x) => x.id === s.id) ?? null;

  const [events, setEvents] = useState<{ events: ActivityEvent[]; since: string } | null>(null);
  const [eventsErr, setEventsErr] = useState<string | null>(null);
  const latestEvents = useLatest();
  const loadEvents = useCallback(() => {
    const isLatest = latestEvents();
    api.storeActivity(s.id)
      .then((r) => { if (isLatest()) { setEvents(r); setEventsErr(null); } })
      .catch((e) => { if (isLatest()) { setEvents(null); setEventsErr((e as Error).message); } });
  }, [s.id, latestEvents]);
  useEffect(() => { setEvents(null); setEventsErr(null); loadEvents(); }, [loadEvents]);

  /** Tout ce qu'un geste de la fiche a pu changer : l'annuaire, et les lectures de la page. */
  const refresh = () => { onChanged(); loadBlocks(); loadEvents(); loadSettings(); };

  // ── Ce que la page déduit de ses lectures. ──
  const deviceIds = useMemo(() => new Set(mine.map((d) => d.id)), [mine]);
  const touching = useMemo(() => (blocks ? blocksTouchingStore(blocks.active.filter((b) => !b.liftedAt), s.id, deviceIds, clientId) : []), [blocks, s.id, deviceIds, clientId]);
  const past = useMemo(() => (blocks ? blocksTouchingStore(blocks.history, s.id, deviceIds, clientId) : []), [blocks, s.id, deviceIds, clientId]);
  const storeBlock = touching.find((b) => b.scope === 'store' && b.targetId === s.id) ?? null;
  const locked = touching.some((b) => b.level === 'lock');
  // L'état de synchro du magasin — LE calcul de la Supervision (`storeHealth`) : le plus muet de ses appareils.
  const health = sup?.store ? storeHealth({ ...sup.store, status: s.status }) : null;
  const lagging = laggingDevice(health);
  const rule = settings
    ? { on: 'api.offline_rule' in settings && switchOn('api.offline_rule', settings['api.offline_rule']), warnDays: Number.parseInt(settings['api.offline_warn_days'] ?? '0', 10) || 0, lockDays: Number.parseInt(settings['api.offline_lock_days'] ?? '0', 10) || 0 }
    : null;
  /** La version d'un appareil : celle que la supervision a relevée au dernier contact, sinon celle de l'annuaire. */
  const versionOf = (d: TenantDevice): string | null => {
    if (d.registerNumber != null) {
      const reg = sup?.store?.registers.find((r) => registerKey(r.registerId) === registerKey(d.registerNumber));
      if (reg?.appVersion) return reg.appVersion;
    }
    const term = sup?.store?.terminals.find((t) => t.deviceId === d.id);
    return term?.appVersion ?? d.appVersion;
  };
  const codeOf = (d: TenantDevice) => d.storeCode ?? (d.registerNumber != null ? codeCaisse(String(d.registerNumber)) : '—');
  const mps = mine.filter((d) => d.nature === 'mobile-partner');
  /** La version publique d'une application au store, ou pourquoi elle ne se lit pas — jamais une version devinée. */
  const publicOf = (slug: string): { publicVersion: string | null; publicLabel: string } => {
    if (storeAppsErr) return { publicVersion: null, publicLabel: `relevé du store illisible (${storeAppsErr})` };
    const st = storeApps?.lines.find((l) => l.slug === slug)?.store;
    if (!st) return { publicVersion: null, publicLabel: 'relevé du store en cours' };
    if (st.kind === 'unknown') return { publicVersion: null, publicLabel: `store illisible (${st.error})` };
    if (st.kind === 'absent') return { publicVersion: null, publicLabel: `application absente du store — à créer sous « ${slug} »` };
    return { publicVersion: st.entry.publicVersion, publicLabel: 'rien de déployé au flux public' };
  };
  // Les applications que ce magasin installe, par la NATURE de l'appareil : un dépôt n'a pas de caisse.
  // ⚠️ Les appareils se rangent par nature : une seconde application de caisse demandera de les ranger par application.
  const updateApps = installableApplications()
    .filter((a) => (a.nature === 'caisse' ? !depot : a.nature === 'mobile-partner'))
    .map((a) => ({
      slug: a.slug,
      devices: (a.nature === 'caisse' ? tills : mps).map((d) => ({ code: codeOf(d), version: versionOf(d) })),
      ...publicOf(a.slug),
    }));

  // ── Actions de la page. ──
  const [boModal, setBoModal] = useState(false);
  const [wizard, setWizard] = useState<{ kind: EquipKind; mode: 'add' | 'config'; dev?: TenantDevice } | null>(null);
  const [pairingDev, setPairingDev] = useState<TenantDevice | null>(null);
  const [editing, setEditing] = useState<{ dev: DeviceRow; rows: DeviceRow[] } | null>(null);
  const [returning, setReturning] = useState<DeviceRow | null>(null);
  const [renting, setRenting] = useState<{ dev: DeviceRow; transit: number | null } | null>(null);
  const [historyDev, setHistoryDev] = useState<HistoryTarget | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [danger, setDanger] = useState<'reset' | 'delete' | null>(null);

  /** Ouvre un geste du parc (modifier, retour) : il travaille sur la ligne du REGISTRE, relue à la demande. */
  const withFleetRow = async (id: string, go: (row: DeviceRow, rows: DeviceRow[], transit: number | null) => void) => {
    try {
      const r = await api.devices();
      const row = r.devices.find((x) => x.id === id);
      if (!row) { await alert({ title: 'Appareil introuvable', message: 'Le registre du parc ne connaît plus cet appareil : la fiche va se relire.', tone: 'err' }); refresh(); return; }
      go(row, r.devices, transitOf(r.transitBusinessDays));
    } catch (e) { await alert({ title: 'Registre du parc illisible', message: (e as Error).message, tone: 'err' }); }
  };

  const blockStore = () => {
    if (storeBlock) nav('/blocages', { state: { liftId: storeBlock.id } });
    else nav('/blocages', { state: { preset: { scope: 'store', targetId: s.id, targetLabel: `${s.name} (${clientName})` } } });
  };

  // (Re)déclaration au BO et à l'EDI — actions RÉELLES, rejouables sans risque.
  const [boBusy, setBoBusy] = useState(false);
  const declareBo = async () => {
    setBoBusy(true);
    try {
      const r = await api.declareStoreBo(s.id);
      // ⚠️ Le sens de synchronisation est réécrit avec la déclaration : son échec se DIT, il ne se fond pas dans le succès.
      if (r.syncMode && !r.syncMode.ok) {
        await alert({ title: 'Déclaré au back-office, sens de synchronisation non transmis', message: `${s.name} est déclaré au back-office, mais le sens de synchronisation n’a pas atteint les caisses : ${r.syncMode.note}.`, tone: 'err' });
      } else showToast(`${s.name} (re)déclaré au back-office — les caisses reçoivent la config de sync au prochain scan.`);
      refresh();
    }
    catch (e) { await alert({ title: 'Déclaration au back-office impossible', message: (e as Error).message, tone: 'err' }); }
    finally { setBoBusy(false); }
  };
  const [ediBusy, setEdiBusy] = useState(false);
  const declareEdi = async () => {
    setEdiBusy(true);
    try { await api.declareStoreEdi(s.id); showToast(`${s.name} déclaré à l'EDI — son GLN est publié au feed d'identité de site.`); refresh(); }
    catch (e) { await alert({ title: 'Déclaration à l’EDI impossible', message: (e as Error).message, tone: 'err' }); }
    finally { setEdiBusy(false); }
  };

  /**
   * LA SAUVEGARDE TÉLÉCHARGEABLE — l'archive descend chez celui qui décide.
   * ⚠️ PAS UN LIEN NU : un `<a href>` remplacerait la page par le JSON d'erreur quand la sauvegarde échoue.
   */
  const [dumpBusy, setDumpBusy] = useState(false);
  const telechargerSauvegarde = async (): Promise<boolean> => {
    if (dumpBusy) return false;
    setDumpBusy(true);
    try {
      const { blob, filename } = await api.storeDump(s.id);
      const url = URL.createObjectURL(blob);
      const lien = document.createElement('a');
      lien.href = url; lien.download = filename ?? `${s.gln}.dump`; lien.click();
      URL.revokeObjectURL(url);
      showToast(`Sauvegarde de ${s.name} téléchargée (${fmtNb(blob.size / 1024 / 1024)} Mo).`);
      return true;
    } catch (e) {
      await alert({ title: 'Sauvegarde impossible', message: (e as Error).message, tone: 'err' });
      return false;
    } finally { setDumpBusy(false); }
  };

  /**
   * AVANT UN GESTE IRRÉVERSIBLE, LA SAUVEGARDE EST PROPOSÉE — puis la modale demande le motif et la recopie du nom.
   * ⚠️ L'ORDRE EST LE SUJET : la sauvegarde d'abord, la destruction ensuite ; l'inverse offrirait l'archive d'une base qui
   *    n'existe plus. Renoncer à l'archive reste possible, mais se DIT. Un dump raté arrête tout.
   */
  const proposeBackupThen = async (what: 'reset' | 'delete') => {
    const veutArchive = await confirm({
      title: what === 'delete' ? `Sauvegarder ${s.name} avant de le supprimer ?` : `Sauvegarder ${s.name} avant de réinitialiser ?`,
      message: what === 'delete'
        ? 'La suppression détruit la base du magasin, ses INSTANTANÉS de migration et son rôle Postgres. Après elle, il ne reste RIEN sur les serveurs — la sauvegarde que vous téléchargez ici sera la seule copie.\n\nTélécharger maintenant ?'
        : 'La réinitialisation supprime TOUT le contenu de la base et la recrée vierge. La sauvegarde que vous téléchargez ici en sera la seule copie.',
      confirmLabel: 'Télécharger la sauvegarde',
      cancelLabel: 'Continuer sans sauvegarde',
    });
    if (veutArchive && !(await telechargerSauvegarde())) return; // échec du dump = on ne détruit rien
    setDanger(what);
  };

  /** RÉINITIALISER LA BASE — la dropper, la recréer vierge. Le serveur refuse dès la première vente (`store-sales.ts`). */
  const reinitialiser = async (reason: string): Promise<boolean> => {
    try {
      const r = await api.resetStoreDb(s.id, s.name, reason);
      // ⚠️ LE REGISTRE PASSE AVANT LES IMAGES, ET C'EST L'ORDRE DE GRAVITÉ. Des images orphelines coûtent du disque ; un
      //    magasin NON DÉCLARÉ au registre du back-office a des caisses qui tombent en 401 au premier cycle.
      if (r.boDeclared === false) {
        await alert({
          title: 'Base réinitialisée, magasin NON DÉCLARÉ au back-office',
          message: `La base de « ${r.name} » est recréée (${r.seededRefs} référentiel(s) semé(s)), mais le magasin n'a pas pu être réinscrit au registre du back-office : ${r.boError ?? 'motif inconnu'}. `
            + 'Tant que ce n’est pas fait, ses caisses seront refusées à la synchronisation. Utilisez « Déclarer au back-office » (section Données) dès que le back-office répond — le geste est rejouable sans risque.'
            + (r.mediaError ? ` (Ses images n’ont pas été purgées non plus : ${r.mediaError}.)` : ''),
          tone: 'err',
        });
      } else if (r.identityError || r.identityMissing?.length) {
        // ⚠️ APRÈS le registre, AVANT les images : une caisse qui scelle sans identité légale produit des pièces sans
        //    raison sociale ni SIRET — plus grave qu'un disque encombré, moins qu'une caisse refusée.
        await alert({
          title: r.identityError ? 'Base réinitialisée, identité légale NON descendue' : 'Base réinitialisée, identité légale incomplète',
          message: (r.identityError
            ? `La base de « ${r.name} » est recréée, mais son identité légale n'a pas pu descendre : ${r.identityError}. `
            : `La base de « ${r.name} » est recréée, mais son dossier ne porte pas : ${r.identityMissing!.join(', ')}. `)
            + 'Ses tickets seraient scellés sans ces mentions. Complétez le dossier si besoin, puis « Déclarer au back-office » (section Données) — le geste est rejouable sans risque.'
            + (r.mediaError ? ` (Ses images n’ont pas été purgées non plus : ${r.mediaError}.)` : ''),
          tone: 'err',
        });
      } else if (r.mediaError) {
        await alert({
          title: 'Base réinitialisée, images non purgées',
          message: `La base de « ${r.name} » est recréée (${r.seededRefs} référentiel(s) semé(s)), mais ses images n'ont pas pu être purgées du stockage d'objets : ${r.mediaError}. Relancer la réinitialisation réessaiera la purge — la base, vide, sera recréée à l'identique, et les caisses appairées entre-temps devront l'être à nouveau.`,
          tone: 'err',
        });
      } else {
        showToast(`Base de « ${r.name} » réinitialisée — ${r.seededRefs} référentiel(s) semé(s), ${r.mediaRemoved ?? 0} image(s) purgée(s).`);
      }
      refresh();
      return true;
    } catch (e) {
      // ⚠️ LE MESSAGE RÉEL : un refus (une vente existe), « base supprimée mais recréation échouée » et un échec avant
      //    tout geste ne se rattrapent pas pareil. On relit ensuite : c'est le serveur qui sait dans quel état est le magasin.
      await alert({ title: 'Réinitialisation impossible', message: e instanceof Error ? e.message : String(e), tone: 'err' });
      refresh();
      return false;
    }
  };

  /** SUPPRIMER LE MAGASIN — le magasin part, son CLIENT reste ; les appareils rentrent au parc. */
  const supprimerMagasin = async (reason: string): Promise<boolean> => {
    try {
      const r = await api.deleteStore(s.id, s.name, true, reason);
      showToast(`${r.name} supprimé · ${r.devicesReleased} appareil(s) rendu(s) au parc · ${r.snapshotsPurged} instantané(s) détruit(s).`);
      onBackClient(); // le client existe toujours, c'est son magasin qui vient de partir
      return true;
    } catch (e) {
      // ⚠️ Le message RÉEL : « accès non révoqués » et « base subsistante » ne se rattrapent pas au même endroit.
      await alert({ title: 'Suppression incomplète', message: (e as Error).message, tone: 'err' });
      return false;
    }
  };

  const st = storeStatus(s.status);
  const sec = sectionOf(section);
  const syncFact = !active ? { v: 'pas encore de base', tone: 'ne' as Tone }
    : supErr ? { v: 'illisible', tone: 'wa' as Tone }
    : !sup ? { v: 'lecture…', tone: 'ne' as Tone }
    : !health ? { v: 'inconnu du back-office', tone: 'wa' as Tone }
    : { v: `${health.label}${health.worst && (health.kind === 'da' || health.kind === 'wa') ? ` · ${fmtDuration(health.worst.hours)}` : ''}`, tone: healthTone(health.kind) as Tone };
  const navCount = (k: StoreSection): { n: number; tone: 'wa' | 'da' | null } | null => {
    if (k === 'equip') return mine.length ? { n: mine.length, tone: null } : null;
    if (k === 'blocks') return touching.length ? { n: touching.length, tone: locked ? 'da' : 'wa' } : null;
    return null;
  };

  return (
    <div ref={rootRef}>
      <nav aria-label="Fil d’Ariane" className="sp-crumbs">
        <button type="button" onClick={onBackClients}>Clients</button><span className="sep">›</span>
        <button type="button" onClick={onBackClient}>{clientName}</button><span className="sep">›</span>
        <span className="here">{s.name}</span>
      </nav>

      {/* Le titre de la section ouverte, et les actions de la page. */}
      <div className="sp-head">
        <div className="title">
          <h1 className="page-title">{sec.title}</h1>
          <div className="desc">{sec.desc}</div>
        </div>
        <div className="sp-actions">
          {canCrossTenant && (
            <button type="button" className="sp-btn-bo" disabled={!active || !s.boDeclaredAt} onClick={() => setBoModal(true)}
              title={!active ? "Ce magasin n'a pas encore de base : il n'a pas de back-office à ouvrir."
                : !s.boDeclaredAt ? "Ce magasin n'est pas déclaré au back-office : déclarez-le d'abord (section Données)."
                : 'Ouvre le back-office de CE magasin dans un nouvel onglet, avec votre compte Orgazone. Un motif est demandé ; la session est journalisée.'}>
              Ouvrir le back-office<Icon name="external" size={14} />
            </button>
          )}
          <Popover
            open={addOpen}
            onOpenChange={setAddOpen}
            id={`sp-add-${s.id}`}
            label="Ajouter un équipement"
            geom={{ width: 280, align: 'end', offsetY: 6 }}
            style={{ padding: 6, borderRadius: 'var(--r-md)', background: 'var(--panel)', border: '1px solid var(--line)', boxShadow: '0 18px 50px -18px rgba(0,0,0,.55)' }}
            trigger={(a) => (
              <button type="button" className="btn-cta" ref={a.ref} onClick={a.onClick} aria-expanded={a['aria-expanded']} aria-haspopup={a['aria-haspopup']} aria-controls={a['aria-controls']}>
                <Icon name="plus" size={14} />Ajouter un équipement<Icon name="chevronD" size={12} />
              </button>
            )}
          >
            {({ close }) => (
              <>
                {/* Ce qui s'ajoute ici : une caisse (jamais sur un dépôt, qui n'encaisse pas) ou un Mobile Partner. */}
                {!depot && (
                  <button type="button" className="sp-menu-item" onClick={() => { close(); setWizard({ kind: 'caisse', mode: 'add' }); }}>
                    <span className="ico"><Icon name="tabletWide" size={16} /></span>
                    <span style={{ minWidth: 0 }}><span className="lbl">Une caisse Sliderz</span><span className="sub">Reçoit son n° de caisse et son QR de mise en service</span></span>
                  </button>
                )}
                <button type="button" className="sp-menu-item" onClick={() => { close(); setWizard({ kind: 'mobile-partner', mode: 'add' }); }}>
                  <span className="ico"><Icon name="barcode-frame" size={16} /></span>
                  <span style={{ minWidth: 0 }}><span className="lbl">Un Mobile Partner</span><span className="sub">Terminal de stock : réceptions, inventaire, transferts</span></span>
                </button>
              </>
            )}
          </Popover>
          {/* ⚠️ LE VERBE SUIT L'ÉTAT : proposer « Bloquer » sur un magasin déjà bloqué est un mensonge d'écran (§23.0). */}
          {canBlockStore && (
            <button type="button" className={storeBlock ? 'btn-ghost' : 'btn-ghost danger'} onClick={blockStore}>
              <Icon name="ban" size={14} />{storeBlock ? 'Lever le blocage' : 'Bloquer'}
            </button>
          )}
        </div>
      </div>

      <div className={`sp-grid ${layout}`}>
        {/* ── Les sections : le second niveau de menu, propre à la page. ── */}
        <nav aria-label="Sections du magasin" className="sp-nav">
          <div className="dcard">
            <div className="sp-navlist" role="tablist" aria-orientation={layout === 'one' ? 'horizontal' : 'vertical'}>
              {STORE_SECTIONS.map((x, i) => {
                const c = navCount(x.key);
                return (
                  <button key={x.key} id={`${tabsId}-tab-${x.key}`} type="button" role="tab" aria-selected={section === x.key} aria-controls={`${tabsId}-panel`}
                    className="sp-tab" onClick={() => setSection(x.key)} tabIndex={section === x.key ? 0 : -1} onKeyDown={(e) => onTabKey(e, i)}>
                    <span className="ico"><Icon name={x.icon} size={15} /></span>
                    <span className="lbl">{x.title}</span>
                    {c && <span className={c.tone ? `sp-count ${c.tone}` : 'sp-count'}>{c.n}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Le panneau de la section ouverte. ⚠️ Pas un `<main>` : la coquille de la Console porte déjà le sien. */}
        <div className="sp-main" role="tabpanel" id={`${tabsId}-panel`} aria-labelledby={`${tabsId}-tab-${section}`}>
          {section === 'overview' && (
            <OverviewSection
              store={s} depot={depot} tills={tills} mine={mine} touching={touching} locked={locked}
              alertBox={overviewAlert({ blocks: touching, lagging, rule })} go={setSection}
              health={health} supErr={supErr} counters={counters} countersErr={countersErr}
              events={events} eventsErr={eventsErr} updateApps={updateApps}
            />
          )}
          {section === 'equip' && (
            <EquipmentSection
              mine={mine} depot={depot} sup={sup?.store ?? null} blocks={touching} canBlock={canBlockStore}
              codeOf={codeOf} versionOf={versionOf}
              onPair={setPairingDev}
              onReconfigure={(d) => setWizard({ kind: 'mobile-partner', mode: 'config', dev: d })}
              onEdit={(d) => void withFleetRow(d.id, (row, rows) => setEditing({ dev: row, rows }))}
              onReturn={(d) => void withFleetRow(d.id, (row) => setReturning(row))}
              onRental={(d) => void withFleetRow(d.id, (row, _rows, transit) => setRenting({ dev: row, transit }))}
              onHistory={setHistoryDev}
              onBlock={(d) => {
                const blk = touching.find((b) => b.scope === 'device' && b.targetId === d.id);
                if (blk) nav('/blocages', { state: { liftId: blk.id } });
                else nav('/blocages', { state: { preset: { scope: 'device', targetId: d.id, targetLabel: `${deviceRef(d)} · ${codeOf(d)} (${s.name})` } } });
              }}
            />
          )}
          {section === 'updates' && <StoreUpdatesPanel storeId={s.id} apps={updateApps} />}
          {section === 'settings' && (
            <SettingsSection
              store={s} depot={depot} active={active} tenantTrade={tenantTrade ?? null} siblings={stores.length}
              settings={settings} settingsErr={settingsErr} onSaved={() => { loadSettings(); loadEvents(); }}
            />
          )}
          {section === 'data' && (
            <>
              <Panel
                title="Base du magasin"
                note="La chaîne de connexion ne quitte jamais le serveur : elle porte le mot de passe du rôle magasin. Le contenu se lit par l’Explorateur."
                actions={active
                  ? <Link to="/explorateur" className="btn-ghost sm">Ouvrir dans l’Explorateur</Link>
                  : s.status === 'provisioning'
                    ? <button type="button" className="btn-cta sm" onClick={onProvision} disabled={provBusy}>{provBusy ? 'Création de la base…' : 'Mettre en service (créer la base)'}</button>
                    : null}
              >
                {/* ⚠️ Le SERVEUR précède la base, du contenant au contenu — c'est l'ordre dans lequel on y accède. */}
                <Row k="Serveur" mono>{s.dbHost ?? (active ? 'emplacement illisible' : 'à la mise en service')}</Row>
                <Row k="Base" mono>{active ? dbName : `${dbName} (à créer)`}</Row>
                <Row k="Schéma" mono note={migRow?.error ?? migErr ?? undefined}>
                  {!active ? '—'
                    : migErr ? 'inconnu'
                    : !mig ? 'lecture…'
                    : migRow?.error ? 'base injoignable'
                    : migRow ? <>v{migRow.version} / v{mig.target} <Pill tone={migRow.upToDate ? 'ok' : 'wa'}>{migRow.upToDate ? 'à jour' : 'en attente de migration'}</Pill> <Link to="/migrations" className="sp-link">Migrations →</Link></>
                    : '—'}
                </Row>
                <Row k="Taille" mono note={countersErr ?? undefined}>
                  {!active ? '—' : countersErr ? 'illisible' : !counters ? 'lecture…' : counters.dbSizeBytes != null ? fmtBytes(counters.dbSizeBytes) : 'non mesurée'}
                </Row>
              </Panel>

              <Panel title="Déclarations" note="Le magasin doit être connu du back-office et de l’EDI. Redéclarer remet son inscription à jour, sans rien effacer.">
                <Row k="Back-office"
                  action={active ? <button type="button" className="btn-ghost sm" disabled={boBusy} onClick={() => void declareBo()}>{boBusy ? 'Déclaration…' : s.boDeclaredAt ? 'Redéclarer au back-office' : 'Déclarer au back-office'}</button> : undefined}>
                  <Pill tone={s.boDeclaredAt ? 'ok' : 'wa'}>{s.boDeclaredAt ? 'déclaré' : 'non déclaré'}</Pill>
                  {s.boDeclaredAt && <span className="mono" style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', fontWeight: 400, marginLeft: 8, whiteSpace: 'nowrap' }}>le {dateTime(s.boDeclaredAt)}</span>}
                </Row>
                {/* ⚠️ SANS DATE, LE MAGASIN N'EST PAS DIT « déclaré » : la Console ne gardait pas cette date avant la migration
                    0056, et une déclaration non enregistrée ne se distingue pas d'une déclaration jamais faite. Redéclarer
                    est sans risque (l'EDI fait un upsert par GLN) et pose la date. */}
                <Row k="EDI (identité de site)"
                  note={s.ediDeclaredAt ? undefined : 'Aucune déclaration enregistrée par la Console : redéclarer est sans risque, et pose la date.'}
                  action={active ? <button type="button" className="btn-ghost sm" disabled={ediBusy} onClick={() => void declareEdi()}>{ediBusy ? 'Déclaration…' : s.ediDeclaredAt ? 'Redéclarer à l’EDI' : 'Déclarer à l’EDI'}</button> : undefined}>
                  <Pill tone={s.ediDeclaredAt ? 'ok' : 'wa'}>{s.ediDeclaredAt ? 'déclaré' : 'aucune déclaration enregistrée'}</Pill>
                  {s.ediDeclaredAt && <span className="mono" style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', fontWeight: 400, marginLeft: 8, whiteSpace: 'nowrap' }}>le {dateTime(s.ediDeclaredAt)}</span>}
                </Row>
              </Panel>

              <BackupsPanel dbName={dbName} active={active} busy={dumpBusy} onDump={() => void telechargerSauvegarde()} />

              {/* ⚠️ MASQUÉ CHEZ UN CLIENT MONO-MAGASIN : il n'existe aucun autre magasin d'où reprendre. */}
              {stores.length > 1 && <StoreTakeoverCard storeId={s.id} storeName={s.name} storeStatus={s.status} />}

              <RepriseCard storeId={s.id} storeCreatedAt={s.createdAt} />

              <Panel title="Zone sensible" danger>
                <Row k={<span style={{ fontWeight: 600, color: 'var(--ink-900)' }}>Réinitialiser la base</span>}
                  action={active ? (
                    <button type="button" className="btn-ghost sm danger"
                      disabled={!counters || counters.sales == null || counters.sales > 0}
                      title={resetWhy(counters, countersErr)}
                      onClick={() => void proposeBackupThen('reset')}>Réinitialiser</button>
                  ) : undefined}>
                  <span style={{ fontWeight: 400, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{active ? resetWhy(counters, countersErr) : 'Pas encore de base : rien à réinitialiser.'}</span>
                </Row>
                <Row k={<span style={{ fontWeight: 600, color: 'var(--ink-900)' }}>Supprimer le magasin</span>}
                  action={<button type="button" className="btn-ghost sm danger" onClick={() => void proposeBackupThen('delete')}>Supprimer</button>}>
                  <span style={{ fontWeight: 400, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>Supprime la base, son inscription au back-office et son site EDI ; les appareils rentrent au parc, le client reste. Une sauvegarde est proposée avant ; le motif et le nom sont demandés.</span>
                </Row>
              </Panel>
            </>
          )}
          {section === 'blocks' && (
            <BlocksSection
              touching={touching} past={past} rule={rule} settingsErr={settingsErr} active={active}
              err={blocksErr} loaded={blocks !== null} canBlock={canBlockStore} onBlock={blockStore}
            />
          )}
          {section === 'history' && (
            <Panel>
              <EventList events={events?.events ?? null} err={eventsErr} />
              {events && <div className="sp-foot">Opérations de la Console sur ce {depot ? 'dépôt' : 'magasin'}. Les événements antérieurs au {events.since} ne portaient pas leur magasin : ils n’apparaissent que dans le journal du client.</div>}
            </Panel>
          )}
        </div>

        {/* ── La carte d'identité : qui est le magasin. Un objet fixe, qui ne grandit jamais avec les fonctions. ── */}
        <aside aria-label="Carte d’identité du magasin" className="sp-id">
          <div className="sp-card">
            <div className="sp-card-band">
              <div className="sp-card-who">
                <span className="ico"><Icon name="store" size={21} /></span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h2>{s.name}</h2>
                  <div className="of">
                    {depot ? 'Dépôt' : 'Point de vente'} de <button type="button" onClick={onBackClient}>{clientName}</button>
                    {' · '}{stores.length > 1 ? `${stores.length} magasins` : 'client mono-magasin'}
                  </div>
                  <div className="pills">
                    <Pill tone={locked ? 'da' : depot ? 'in' : st.k}>{locked ? 'bloqué' : depot ? 'dépôt' : st.label.charAt(0).toLowerCase() + st.label.slice(1)}</Pill>
                    <Pill tone={syncFact.tone}>{active ? `synchro ${syncFact.v}` : syncFact.v}</Pill>
                  </div>
                </div>
              </div>
              <div className="sp-gln">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="k">GLN</div>
                  {/* Le GLN est l'identité FISCALE du magasin (chaîne NF525, nom de sa base) — immuable après création. */}
                  <Copyable value={s.gln} label="Copier le GLN"><span className="v">{s.gln}</span></Copyable>
                </div>
              </div>
            </div>
            <div className="sp-facts">
              <Fact icon="pin" k="Adresse" v={s.address ? [s.address.voie, [s.address.cp, s.address.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—' : '—'} />
              {/* ⚠️ Le radical se montre DANS SON GENCODE (« 2 + radical ») : c'est ce qu'on lit sur une étiquette. */}
              <Fact icon="lock" mono
                k={depot ? 'N° magasin' : 'N° magasin · radical EAN'}
                v={`${s.sCode ? `S${s.sCode}` : '—'}${depot ? '' : ` · ${fullEanRadical(s.radicalEan) ?? '—'}`}`}
                sub="figés à la première vente" />
              <Fact icon="building" mono k="SIRET de l’établissement" v={s.siret ?? '—'} />
              <Fact icon="tag" k="Type de commerce" v={s.businessKind ?? tenantTrade ?? '—'}
                sub={s.businessKind || tenantTrade ? (s.businessKind && s.businessKind !== tenantTrade ? 'propre au magasin' : 'hérité du client') : undefined} />
              <Fact icon="pulse" k="Synchronisation" v={syncFact.v} tone={syncFact.tone} />
              <Fact icon="ban" k="Blocages" v={blocksErr ? 'illisibles' : !blocks ? 'lecture…' : touching.length ? `${touching.length} en cours` : 'aucun'}
                tone={blocksErr ? 'wa' : touching.length ? (locked ? 'da' : 'wa') : 'ok'} />
            </div>
          </div>
        </aside>
      </div>

      {boModal && <OpenBackofficeModal store={{ id: s.id, name: s.name }} onClose={() => setBoModal(false)} />}
      {danger === 'reset' && (
        <ReasonModal
          title={`Réinitialiser la base de « ${s.name} »`}
          intro="TOUT le contenu de cette base est supprimé — catalogue, stock, clients — puis elle est recréée vierge, avec les référentiels du métier. Le magasin garde son GLN, son code et son radical EAN. Action irréversible."
          requireName={s.name} confirmLabel="Réinitialiser la base" busyLabel="Réinitialisation…" danger
          onClose={() => setDanger(null)} onConfirm={reinitialiser} />
      )}
      {danger === 'delete' && (
        <ReasonModal
          title={`Supprimer « ${s.name} »`}
          intro={`Partent définitivement : la base et toutes ses données fiscales · les instantanés de migration · le rôle Postgres · l’inscription au back-office (donc la clé d’API des caisses) · le site EDI et ses liens fournisseurs. Les appareils rentrent au parc ; le client « ${clientName} » n’est pas touché. Action irréversible.`}
          requireName={s.name} confirmLabel="Supprimer le magasin" busyLabel="Suppression…" danger
          onClose={() => setDanger(null)} onConfirm={supprimerMagasin} />
      )}
      {wizard && (
        <EquipmentWizard
          store={s} stores={stores} tenantName={clientName}
          mode={wizard.mode} initialKind={wizard.kind}
          presetDevice={wizard.dev ? { id: wizard.dev.id, name: wizard.dev.name, family: wizard.dev.family, model: wizard.dev.model, serial: wizard.dev.serial } : undefined}
          onClose={() => setWizard(null)}
          onDone={(m) => { setWizard(null); showToast(m); refresh(); }} />
      )}
      {/* ⚠️ Réappairage = LE MÊME assistant, ouvert à son étape 4 — pas une seconde modale QR. */}
      {pairingDev && (
        <EquipmentWizard
          store={s} stores={stores} tenantName={clientName} mode="pair" initialKind="caisse"
          presetDevice={{ id: pairingDev.id, name: pairingDev.name, family: pairingDev.family, model: pairingDev.model, serial: pairingDev.serial }}
          registerNumber={pairingDev.registerNumber}
          tokenValidUntil={pairingDev.lastSeenAt ? null : pairingDev.pairingExpiresAt}
          onClose={() => setPairingDev(null)}
          onDone={(m) => { setPairingDev(null); showToast(m); refresh(); }} />
      )}
      {editing && <DeviceForm rows={editing.rows} device={editing.dev} onClose={() => setEditing(null)} onDone={(m) => { setEditing(null); showToast(m); refresh(); }} />}
      {returning && <ReturnDevice dev={returning} onClose={() => setReturning(null)} onDone={(m) => { setReturning(null); showToast(m); refresh(); }} />}
      {renting && <RentalModal dev={renting.dev} transit={renting.transit} onClose={() => setRenting(null)} onDone={refresh} />}
      {historyDev && <DeviceHistoryModal dev={historyDev} onClose={() => setHistoryDev(null)} />}
    </div>
  );
}

/** Pourquoi la base peut, ou ne peut pas, être réinitialisée — la même règle que le refus du serveur (`store-sales.ts`). */
function resetWhy(c: Awaited<ReturnType<typeof api.storeCounters>> | null, err: string | null): string {
  if (err) return `Impossible de vérifier qu’aucune vente n’a été enregistrée (${err}) : la réinitialisation reste fermée.`;
  if (!c) return 'Vérification des ventes…';
  if (c.sales == null) return `Impossible de vérifier qu’aucune vente n’a été enregistrée${c.salesError ? ` (${c.salesError})` : ''} : la réinitialisation reste fermée.`;
  if (c.sales > 0) return `Impossible : ${c.sales} pièce${c.sales > 1 ? 's' : ''} de vente ${c.sales > 1 ? 'sont enregistrées' : 'est enregistrée'} dans ce magasin. Une base fiscale se conserve ; seule la suppression reste possible.`;
  return 'Aucune vente enregistrée : la base peut revenir à son état de mise en service. Le motif et le nom sont demandés.';
}

/** Un fait de la carte d'identité. */
function Fact({ icon, k, v, sub, mono, tone }: { icon: IconName; k: string; v: string; sub?: string; mono?: boolean; tone?: Tone }) {
  const cls = ['v', mono ? 'mono' : '', tone === 'ok' || tone === 'wa' || tone === 'da' ? tone : ''].filter(Boolean).join(' ');
  return (
    <div className="sp-fact">
      <span className="ico"><Icon name={icon} size={13} /></span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="k">{k}</div>
        <div className={cls}>{v}</div>
        {sub && <div className="sub">{sub}</div>}
      </div>
    </div>
  );
}

/** La pastille d'un événement du journal, par famille. */
const FAMILY_TONE: Record<ActivityEvent['family'], string> = {
  provisioning: 'var(--ok-fg)', flotte: 'var(--in-fg)', blocages: 'var(--da-fg)', 'données': 'var(--wa-fg)', clients: 'var(--vi-fg)', plateforme: 'var(--ink-500)',
};

/** Une liste d'événements du journal de la Console — `null` pendant la lecture. */
function EventList({ events, err, limit }: { events: ActivityEvent[] | null; err: string | null; limit?: number }) {
  if (err) return <ReadError>Historique illisible : {err}</ReadError>;
  if (!events) return <div className="sp-empty">Lecture du journal…</div>;
  if (events.length === 0) return <div className="sp-empty">Aucune opération de la Console sur ce magasin.</div>;
  return (
    <>
      {(limit ? events.slice(0, limit) : events).map((e, i) => (
        <div key={`${e.at}-${i}`} className="sp-event">
          <span className="dot" style={{ background: FAMILY_TONE[e.family] ?? 'var(--ink-500)' }} />
          <div className="body"><div className="txt">{e.text}</div>{e.meta && <div className="meta">{e.meta}</div>}</div>
          <span className="at">{dateTimeShort(e.at)}</span>
        </div>
      ))}
    </>
  );
}

// ── VUE D'ENSEMBLE : une alerte (la plus importante), quatre indicateurs, les compteurs, l'activité récente. ──
function OverviewSection({ store: s, depot, tills, mine, touching, locked, alertBox, go, health, supErr, counters, countersErr, events, eventsErr, updateApps }: {
  store: TenantStore; depot: boolean; tills: TenantDevice[]; mine: TenantDevice[]; touching: BlockRow[]; locked: boolean;
  alertBox: ReturnType<typeof overviewAlert>; go: (s: StoreSection) => void;
  health: StoreHealth | null;
  supErr: string | null;
  counters: Awaited<ReturnType<typeof api.storeCounters>> | null; countersErr: string | null;
  events: { events: ActivityEvent[] } | null; eventsErr: string | null;
  updateApps: { devices: { version: string | null }[]; publicVersion: string | null }[];
}) {
  const active = s.status === 'active';
  // Caisses EN LIGNE : celles dont la supervision a reçu un contact sous le seuil de silence (`storeHealth`).
  // ⚠️ Les caisses jamais appairées se nomment À PART : « 2 / 3 » sans elles laisserait chercher une panne.
  const online = health ? health.tillsOnline : null;
  const neverPaired = tills.filter((d) => !d.lastSeenAt).length;
  const muted = health && (health.kind === 'da' || health.kind === 'wa') ? health.worst : null;
  // ⚠️ « À jour » se juge seulement contre une version CONNUE (le flux public de la caisse) : le reste se dit non mesuré.
  const retail = updateApps[0];
  const behind = !depot && retail?.publicVersion ? retail.devices.filter((d) => d.version !== retail.publicVersion).length : null;
  const stats: { k: string; v: string; sub: string; tone?: 'ok' | 'wa' | 'da' | 'ne' }[] = [
    {
      k: 'Synchro',
      v: !active ? '—' : supErr ? '?' : !health ? '…' : muted ? fmtDuration(muted.hours) : health.label,
      sub: !active ? 'pas encore de base' : supErr ? 'supervision illisible' : !health ? '' : muted ? `${muted.code} ${health.kind === 'da' ? 'hors ligne' : 'silencieux'}` : health.worst ? `plus ancien contact ${timeAgo(new Date(health.worst.at).toISOString())}` : (health.reason ?? 'aucun contact'),
      tone: health ? healthTone(health.kind) : 'ne',
    },
    depot
      ? { k: 'Appareils', v: String(mine.length), sub: mine.length ? 'affectés au dépôt' : 'aucun appareil' }
      : { k: 'Caisses', v: online === null ? String(tills.length) : `${online} / ${tills.length}`, sub: !tills.length ? 'aucune caisse' : online === null ? 'en ligne : non mesuré' : `en ligne${neverPaired ? ` · ${neverPaired} jamais appairée${neverPaired > 1 ? 's' : ''}` : ''}` },
    { k: 'Mises à jour', v: behind === null ? '—' : String(behind), sub: behind === null ? 'version attendue non mesurée' : behind ? `caisse${behind > 1 ? 's' : ''} en retard sur le flux public` : 'caisses à jour du flux public', tone: behind === null ? 'ne' : behind ? 'wa' : 'ok' },
    { k: 'Blocages', v: String(touching.length), sub: touching.length ? (locked ? 'blocage en cours' : 'avertissement en cours') : 'aucun en cours', tone: touching.length ? (locked ? 'da' : 'wa') : 'ok' },
  ];
  const table = counters ? counterTable(counters.rows, counters.clientSeq ?? []) : null;
  return (
    <>
      {alertBox && (
        <div className={`sp-alert ${alertBox.tone}`} role="status">
          <span className="ico"><Icon name="alert" size={16} /></span>
          <div className="body"><div className="ttl">{alertBox.title}</div><div className="txt">{alertBox.text}</div></div>
          <button type="button" onClick={() => go(alertBox.go)}>{alertBox.cta}</button>
        </div>
      )}
      <section className="sp-panel sp-stats">
        {stats.map((x) => (
          <div key={x.k} className="sp-stat">
            <div className="k">{x.k}</div>
            <div className={x.tone ? `v ${x.tone}` : 'v'}>{x.v}</div>
            <div className="sub">{x.sub}</div>
          </div>
        ))}
      </section>
      <Panel title="Compteurs" note="Dernier numéro attribué, caisse par caisse. Tous numérotés magasin · caisse : aucune remise à zéro possible.">
        {depot ? <div className="sp-empty">Un dépôt ne vend pas : il n’a ni caisse, ni compteur fiscal.</div>
          : !active ? <div className="sp-empty">Pas encore de base : aucun compteur n’est ouvert. Le premier ticket ouvrira la séquence.</div>
          : countersErr ? <ReadError>Compteurs illisibles (base injoignable) : {countersErr}</ReadError>
          : !table ? <div className="sp-empty">Lecture des séquences…</div>
          : table.cols.length === 0 ? <div className="sp-empty">Aucune séquence ouverte : le premier ticket ouvrira la numérotation.</div>
          : (
            <TableScroll className="sp-table">
              <thead><tr><th>Document</th>{table.cols.map((c) => <th key={c} className="r">{codeCaisse(c)}</th>)}<th className="r">Total</th></tr></thead>
              <tbody>
                {table.rows.map((r) => (
                  <tr key={r.label}>
                    <td><div style={{ fontSize: 'var(--t-lead)', fontWeight: 600 }}>{r.label}</div><div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{r.nature}</div></td>
                    {r.cells.map((v, i) => <td key={table.cols[i]} className="num">{v == null ? '—' : String(v).padStart(6, '0')}</td>)}
                    <td className="num total">{fmtNb(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </TableScroll>
          )}
        {/* ⚠️ LA LIGNE CLIENTS DIT DES NUMÉROS ; L'EFFECTIF EST UN AUTRE CHIFFRE, dit à part — confondus, « 1 240 » se lirait
            « dernière fiche n° 1 240 ». `null` = table absente : « non lisible », jamais « 0 » (§23.0). */}
        {counters && !depot && (
          <div className="sp-foot">
            Fiches clients actives : <b className="mono">{counters.clients == null ? 'non lisible' : fmtNb(counters.clients)}</b>.
            {s.sCode && <> Pièces : <span className="mono">T-S{s.sCode}R&lt;caisse&gt;-000000</span> · clients : <span className="mono">CLI-S{s.sCode}R&lt;caisse&gt;-000000</span> (<span className="mono">R00</span> est réservé au serveur).</>}
          </div>
        )}
      </Panel>
      <Panel title="Activité récente" actions={<button type="button" className="sp-link" onClick={() => go('history')}>Tout l’historique →</button>}>
        <EventList events={events?.events ?? null} err={eventsErr} limit={4} />
      </Panel>
    </>
  );
}

/** « J-12 · 08/11/26 » — l'échéance d'un appareil loué, colorée quand elle approche ou qu'elle est dépassée. */
function dueOf(end: string | null): { text: string; tone: '' | 'wa' | 'da' } {
  if (!end) return { text: 'sans échéance', tone: '' };
  const days = Math.ceil((new Date(end).getTime() - Date.now()) / 86_400_000);
  const d = new Date(end);
  const date = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear() % 100).padStart(2, '0')}`;
  return { text: `${days < 0 ? `J+${-days}` : `J-${days}`} · ${date}`, tone: days < 0 ? 'da' : days < 30 ? 'wa' : '' };
}

// ── ÉQUIPEMENTS : un tableau tenu sans défilement dès 560 px. ──
function EquipmentSection({ mine, depot, sup, blocks, canBlock, codeOf, versionOf, onPair, onReconfigure, onEdit, onReturn, onRental, onHistory, onBlock }: {
  mine: TenantDevice[]; depot: boolean; sup: SupervisionStore | null; blocks: BlockRow[]; canBlock: boolean;
  codeOf: (d: TenantDevice) => string;
  /** La version relevée par la supervision, sinon celle de l'annuaire — la MÊME lecture que la section Mises à jour. */
  versionOf: (d: TenantDevice) => string | null;
  onPair: (d: TenantDevice) => void; onReconfigure: (d: TenantDevice) => void; onEdit: (d: TenantDevice) => void;
  onReturn: (d: TenantDevice) => void; onBlock: (d: TenantDevice) => void;
  /** Corriger le régime et les dates de l'appareil, sans retour au parc. */
  onRental: (d: TenantDevice) => void;
  /** L'historique de l'appareil : les clients et les magasins où il est passé. */
  onHistory: (d: TenantDevice) => void;
}) {
  // Les caisses d'abord, dans l'ordre de leur numéro ; les autres appareils ensuite.
  const ordered = [...mine].sort((a, b) => (a.registerNumber == null ? 1 : 0) - (b.registerNumber == null ? 1 : 0) || (a.registerNumber ?? 0) - (b.registerNumber ?? 0));
  return (
    <Panel>
      {ordered.length === 0 ? (
        <EmptyState icon="cash"
          title={depot ? 'Aucun appareil au dépôt' : 'Aucun équipement'}
          note={depot ? 'Un Mobile Partner s’y ajoute pour les réceptions et l’inventaire.' : 'Le magasin ne peut pas encore encaisser : ajoutez une caisse depuis « Ajouter un équipement ».'} />
      ) : (
        <TableScroll className="sp-table equip">
          <thead><tr><th>Appareil</th><th>Régime</th><th>État</th><th className="r">Actions</th></tr></thead>
          <tbody>
            {ordered.map((d) => {
              const till = d.registerNumber != null;
              const mp = d.nature === 'mobile-partner';
              const r = d.rentalType === 'courte' || d.rentalType === 'longue' || d.rentalType === 'achat' ? REGIME[d.rentalType] : null;
              const due = r && d.rentalType !== 'achat' ? dueOf(d.rentalEnd) : { text: 'sans échéance', tone: '' as const };
              // Le dernier contact vient de la supervision (le battement relevé par le BO), sinon de l'annuaire.
              const seen = till
                ? sup?.registers.find((x) => registerKey(x.registerId) === registerKey(d.registerNumber))?.lastSeenAt ?? null
                : sup?.terminals.find((x) => x.deviceId === d.id)?.lastSeenAt ?? null;
              const seenIso = seen != null ? new Date(seen).toISOString() : d.lastSeenAt;
              const blk = blocks.find((b) => b.scope === 'device' && b.targetId === d.id);
              const state = blk ? { label: blk.level === 'lock' ? 'bloquée' : 'avertie', tone: (blk.level === 'lock' ? 'da' : 'wa') as Tone }
                : d.lastSeenAt ? { label: 'en service', tone: 'ok' as Tone }
                : d.pairingExpiresAt ? { label: 'QR en attente', tone: 'in' as Tone }
                : { label: 'jamais vue', tone: 'ne' as Tone };
              const ident = d.name ?? d.serial ?? d.id.slice(0, 8);
              const icon: IconName = till ? 'tabletWide' : mp ? 'barcode-frame' : (d.nature ? NATURE_ICON[d.nature] : 'tabletWide');
              const pairTitle = d.lastSeenAt ? 'Réappairer — un jeton neuf révoque l’ancien' : d.pairingExpiresAt ? `Voir le QR de mise en service (${expiresIn(d.pairingExpiresAt)})` : 'Générer le QR de mise en service';
              return (
                <tr key={d.id}>
                  <td>
                    <div className="sp-dev">
                      <span className="ico"><Icon name={icon} size={17} /></span>
                      <div style={{ minWidth: 0 }}>
                        <div className="head"><span className="no">{codeOf(d)}</span><span className="name"><DeviceName d={d} /></span></div>
                        <div className="kind"><span>{d.family}{d.model ? ' ·' : ''}</span>{d.model && <span className="sp-nowrap">{d.model}</span>}</div>
                        <div className="ids"><span style={{ overflowWrap: 'anywhere' }}>{d.serial ?? 'sans n° de série'}</span>{versionOf(d) && <span className="sp-nowrap">· v{versionOf(d)}</span>}</div>
                      </div>
                    </div>
                  </td>
                  <td className="sp-nowrap">
                    {r ? <span className="sp-pill" style={{ background: r.bg, color: r.fg }}>{r.label.toLowerCase()}</span> : <Pill tone="ne">affecté</Pill>}
                    <div className={due.tone ? `sp-due ${due.tone}` : 'sp-due'}>{due.text}</div>
                  </td>
                  <td className="sp-nowrap">
                    <Pill tone={state.tone}>{state.label}</Pill>
                    <div className="sp-seen">{seenIso ? `synchro ${timeAgo(seenIso)}` : '—'}</div>
                  </td>
                  <td style={{ width: '1%' }}>
                    <div className="sp-acts">
                      {/* Le premier geste dépend de l'appareil : le QR pour une caisse, la configuration pour un Mobile Partner
                          (ses droits et magasins accessibles), rien pour un appareil qui n'embarque ni l'un ni l'autre. */}
                      {till ? <button type="button" className="btn-icon" title={pairTitle} aria-label={pairTitle} onClick={() => onPair(d)}><Icon name="qr" size={15} /></button>
                        : mp ? <button type="button" className="btn-icon" title="Reconfigurer le terminal" aria-label={`Reconfigurer ${ident}`} onClick={() => onReconfigure(d)}><Icon name="sliders" size={15} /></button>
                        : <span aria-hidden="true" className="slot" />}
                      <button type="button" className="btn-icon" title="Régime et dates" aria-label={`Régime et dates de ${ident}`} onClick={() => onRental(d)}><Icon name="calendar" size={15} /></button>
                      <button type="button" className="btn-icon" title="Historique de l’appareil" aria-label={`Historique de ${ident}`} onClick={() => onHistory(d)}><Icon name="clock" size={15} /></button>
                      <button type="button" className="btn-icon" title="Modifier (formulaire d’entrée au parc)" aria-label={`Modifier ${ident}`} onClick={() => onEdit(d)}><Icon name="edit" size={15} /></button>
                      {/* Kill-switch SA-4.5 : posé depuis la LIGNE de l'appareil, le verbe suit l'état (§23.0). */}
                      {canBlock && (
                        <button type="button" className={blk ? 'btn-icon' : 'btn-icon danger'} title={blk ? 'Lever le blocage de cet appareil' : 'Bloquer cet appareil'} aria-label={blk ? `Lever le blocage de ${ident}` : `Bloquer ${ident}`} onClick={() => onBlock(d)}><Icon name="ban" size={15} /></button>
                      )}
                      <button type="button" className="btn-icon danger" title="Retour au parc" aria-label={`Retour au parc de ${ident}`} onClick={() => onReturn(d)}><Icon name="returnBox" size={15} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TableScroll>
      )}
      <div className="sp-foot">
        Un n° de caisse R… n’est jamais réutilisé après un retour au parc. <Link to="/appareils" className="sp-link">Registre de la flotte →</Link>
      </div>
    </Panel>
  );
}

// ── RÉGLAGES : la synchronisation (réglée par Orgazone), le magasin, les tickets et la communication. ──
function SettingsSection({ store: s, depot, active, tenantTrade, siblings, settings, settingsErr, onSaved }: {
  store: TenantStore; depot: boolean; active: boolean; tenantTrade: string | null; siblings: number;
  settings: Record<string, string> | null; settingsErr: string | null; onSaved: () => void;
}) {
  const mode = syncModeOf(s.syncMode);
  const { user } = useAuth();
  // Changer le sens : verbe réservé au super-admin (`console.syncmode.write`) — le geste n'est offert qu'à lui (§23.0).
  const canSyncMode = can(user, 'console.syncmode.write');
  const [modeAsk, setModeAsk] = useState(false);
  const other = syncModeOf(s.syncMode === 'mono' ? 'bi' : 'mono');
  // Ce que la base du magasin porte RÉELLEMENT, et donc ce que les caisses appliquent — la règle de lecture du contrat
  // (absent = bidirectionnel). `null` = pas lu : on ne prétend ni transmis ni en retard.
  const applied = active && settings ? storeSyncMode(settings['api.sync_mode']) : null;
  const behind = applied !== null && applied !== s.syncMode;
  /** Change le sens : la base du magasin d'abord, l'annuaire ensuite — un refus laisse tout tel quel, avec son motif. */
  const changeMode = async (reason: string): Promise<boolean> => {
    try {
      const r = await api.setStoreSyncMode(s.id, other.k, reason);
      if (r.descend.ok) showToast(`Synchronisation ${other.label.toLowerCase()} — ${r.descend.note}.`);
      else await alert({ title: `Synchronisation ${other.label.toLowerCase()} enregistrée, pas encore transmise`, message: `Le sens est écrit, mais il n’a pas atteint les caisses : ${r.descend.note}.`, tone: 'err' });
      onSaved();
      return true;
    } catch (e) {
      await alert({ title: 'Sens de synchronisation inchangé', message: (e as Error).message, tone: 'err' });
      return false;
    }
  };
  const [autoBusy, setAutoBusy] = useState(false);
  const syncAuto = settings ? switchOn('api.sync_auto', settings['api.sync_auto']) : null;
  /** La synchronisation automatique des caisses — un réglage que la caisse applique à chaque battement. */
  const setSyncAuto = async (next: boolean) => {
    if (!next && !await confirm({
      title: 'Couper la synchronisation automatique des caisses ?',
      message: 'Les caisses de ce magasin cesseront d’échanger d’elles-mêmes : leurs ventes attendront un envoi déclenché en caisse, et le catalogue ne descendra plus seul. La synchro périodique obligatoire continue de compter.',
      confirmLabel: 'Couper',
    })) return;
    setAutoBusy(true);
    try {
      const r = await api.updateStoreSettings(s.id, { 'api.sync_auto': next ? '1' : '0' });
      showToast(`Synchronisation automatique ${next ? 'activée' : 'coupée'} — ${r.descend.note}.`);
      onSaved();
    } catch (e) { await alert({ title: 'Enregistrement impossible', message: (e as Error).message, tone: 'err' }); }
    finally { setAutoBusy(false); }
  };
  const credit = readValidity(settings?.['validity.credit_note']);
  const gift = readValidity(settings?.['validity.gift']);
  const fidOn = settings ? switchOn('fid.enabled', settings['fid.enabled']) : null;
  const unread = !active ? 'pas encore de base' : settingsErr ? 'illisible' : 'lecture…';
  const val = (k: string): string | null => (settings ? (settings[k]?.trim() || null) : null);

  return (
    <>
      <Panel title="Synchronisation" note="Réglée par Orgazone. Une vente naît en caisse et remonte au back-office, jamais l’inverse.">
        {settingsErr && <ReadError>Réglages illisibles dans la base du magasin : {settingsErr}</ReadError>}
        <Row k="Sens"
          note={behind
            ? `${mode.desc} ⚠️ La base du magasin porte encore « ${syncModeOf(applied).label} » : ses caisses l’appliquent. Re-déclarez le magasin au back-office (section Données) pour transmettre le sens choisi.`
            : mode.desc}
          action={canSyncMode ? <button type="button" className="btn-ghost sm" onClick={() => setModeAsk(true)}>Passer en {other.label.toLowerCase()}</button> : undefined}>
          <Pill tone={s.syncMode === 'bi' ? 'ok' : 'wa'}>{mode.label}</Pill>
          {behind && <> <Pill tone="da">non transmis aux caisses</Pill></>}
        </Row>
        <Row k="Synchronisation automatique"
          note={syncAuto === null ? unread : syncAuto ? 'Ventes, clôtures et fiches s’échangent seules à chaque battement de la caisse ; hors ligne, elle rejoue sa file à la reconnexion.' : 'Coupée : les caisses n’échangent plus d’elles-mêmes, leurs ventes attendent un envoi déclenché en caisse.'}
          action={syncAuto === null ? undefined : <Toggle on={syncAuto} label="Synchronisation automatique des caisses" onChange={(v) => void setSyncAuto(v)} disabled={autoBusy} />}>
          {syncAuto === null ? '—' : syncAuto ? 'activée' : 'coupée'}
        </Row>
      </Panel>

      {modeAsk && (
        <ReasonModal
          title={`Passer « ${s.name} » en synchronisation ${other.label.toLowerCase()}`}
          intro={other.k === 'mono'
            ? 'Le siège décidera de tous les réglages du magasin : ses caisses les afficheront en lecture seule, et le serveur refusera qu’un réglage modifié sur place remonte. Les préférences de chaque appareil restent modifiables.'
            : 'Les caisses pourront de nouveau modifier les réglages du magasin, selon les droits de l’opérateur, et ces modifications remonteront au back-office.'}
          confirmLabel={`Passer en ${other.label.toLowerCase()}`} busyLabel="Enregistrement…"
          onClose={() => setModeAsk(false)} onConfirm={changeMode} />
      )}

      {active && settings && <PeriodicPanel storeId={s.id} vals={settings} onSaved={onSaved} />}

      <Panel title="Magasin" note="Ce qui définit le magasin. Figé une fois en service, sauf mention.">
        {/* ⚠️ PAS DE « CHANGER » (décision utilisateur) : un pivot d'activité au même SIRET passe par une
            déclaration légale — donc chez nous par un NOUVEAU magasin. */}
        <Row k="Type de commerce" note={depot ? 'Un dépôt ne vend pas : son assemblage n’embarque ni grille de taille, ni saison.' : 'Détermine les listes injectées dans cette base. Figé après la mise en service : un changement d’activité réel passe par un nouveau magasin.'}>
          {s.businessKind ?? tenantTrade ?? '—'} {(s.businessKind || tenantTrade) && <Pill tone="ne">{s.businessKind && s.businessKind !== tenantTrade ? 'propre au magasin' : 'hérité du client'}</Pill>}
        </Row>
        <Row k="Devise" note="Choisie une fois pour toutes à la mise en service.">{s.currency} <Pill tone="ne">figée</Pill></Row>
        <Row k="Calendrier d’exploitation" note={settings ? `Fuseau ${val('store.timezone') ?? 'non posé — le back-office retombe sur Europe/Paris'}. Le client le règle dans son back-office.` : undefined}>
          {settings ? (openDaysLabel(settings['store.open_days']) ?? 'jours d’ouverture non posés') : unread}
        </Row>
        {!depot && (
          <Row k="Programme de fidélité" note={siblings > 1 ? 'Propre à ce magasin. Le partager entre magasins se décide dans Pilot.' : 'Propre à ce magasin.'}>
            {fidOn === null ? unread : !fidOn ? 'désactivé' : settings?.['fid.mode'] === 'cashback' ? 'Cagnotte' : 'Bons d’achat'}
          </Row>
        )}
      </Panel>

      {!depot && (
        <Panel title="Tickets et titres" note="Posés à la mise en service. Le client les modifie ensuite dans son back-office.">
          <Row k="En-tête de ticket">{settings ? (val('ticket.header') ?? 'non renseigné') : unread}</Row>
          <Row k="Pied de ticket">{settings ? (val('ticket.footer') ?? 'non renseigné') : unread}</Row>
          <Row k="Validité des avoirs" note={credit?.text ? `Message porté : « ${credit.text} »` : 'Un avoir garde la durée en vigueur le jour de son émission.'}>
            {settings ? (credit ? `${credit.val} ${unitWord(credit.val, credit.unit)}` : 'non renseignée') : unread}
          </Row>
          <Row k="Validité des chèques cadeaux" note={gift?.text ? `Message porté : « ${gift.text} »` : undefined}>
            {settings ? (gift ? `${gift.val} ${unitWord(gift.val, gift.unit)}` : 'non renseignée') : unread}
          </Row>
        </Panel>
      )}

      {!depot && active && (
        <Panel title="Communication avec les clients" note="Le compte d’envoi appartient à Orgazone ; le magasin reçoit le droit de s’en servir.">
          <CompteSmsCard gln={s.gln} storeName={s.name} />
          {/* ⚠️ L'expéditeur est celui que la CAISSE lit (`mail.from`) ; aucune vérification n'est faite d'ici, donc
              aucune pastille « vérifié ». */}
          <Row k="Expéditeur des e-mails" mono note={settings && !val('mail.from') ? 'Les e-mails de la caisse partent sans expéditeur propre tant que le client ne l’a pas réglé.' : undefined}>
            {settings ? (val('mail.from') ?? 'non renseigné') : unread}
          </Row>
        </Panel>
      )}
      {/* Le droit d'envoyer, canal par canal — décision d'ÉDITEUR, posée ici et nulle part ailleurs : rangée dans le
          back-office, elle serait levable par celui qu'elle vise. Montée sur un magasin ACTIF seulement. */}
      {!depot && active && <SendRightCard gln={s.gln} />}
    </>
  );
}

// ── Les sauvegardes de la base : celles de l'agent du serveur de bases, et le téléchargement à la demande. ──
function BackupsPanel({ dbName, active, busy, onDump }: { dbName: string; active: boolean; busy: boolean; onDump: () => void }) {
  const [pieces, setPieces] = useState<BackupPiece[] | null>(null);
  const [complete, setComplete] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setPieces(null); setErr(null);
    if (!active) return;
    let alive = true;
    api.backupHistory({ database: dbName, limit: 8 })
      .then((r) => { if (alive) { setPieces(r.pieces); setComplete(r.complete); } })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, [dbName, active]);
  return (
    <Panel
      title="Sauvegardes"
      note="Les archives de cette base déposées par l’agent du serveur de bases. Restaurer ouvre une base temporaire, en lecture seule, à côté de la base vivante — depuis la page Sauvegardes."
      actions={active ? <>
        <button type="button" className="btn-ghost sm" disabled={busy} onClick={onDump} title="Produit une sauvegarde complète (pg_dump) de la base et la télécharge.">{busy ? 'Sauvegarde en cours…' : 'Sauvegarder maintenant (.dump)'}</button>
        <Link to="/sauvegardes" className="btn-ghost sm">Restaurer</Link>
      </> : undefined}
    >
      {!active ? <div className="sp-empty">Pas encore de base : rien à sauvegarder.</div>
        : err ? <ReadError>Historique des sauvegardes illisible : {err}</ReadError>
        : !pieces ? <div className="sp-empty">Lecture des archives…</div>
        : pieces.length === 0 ? <div className="sp-empty">Aucune archive relevée pour cette base : l’agent de sauvegarde ne l’a pas encore déposée.</div>
        : (
          <TableScroll className="sp-table">
            <thead><tr><th>Archive</th><th className="r">Taille</th><th>État</th></tr></thead>
            <tbody>
              {pieces.map((p, i) => (
                <tr key={`${p.at}-${i}`}>
                  <td><div className="mono" style={{ fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap' }}>{dateTimeShort(p.at)}</div><div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{p.host}{p.offsite ? ' · hors site' : ''}</div></td>
                  <td className="num" style={{ fontSize: 'var(--t-dense)' }}>{p.bytes != null ? fmtBytes(p.bytes) : '—'}</td>
                  <td className="sp-nowrap">{p.ok ? <Pill tone="ok">complète</Pill> : <span title={p.error ?? undefined}><Pill tone="da">en échec</Pill></span>}</td>
                </tr>
              ))}
            </tbody>
          </TableScroll>
        )}
      {pieces && !complete && <div className="sp-foot">Seules les plus récentes sont montrées : la liste complète vit dans la page Sauvegardes.</div>}
    </Panel>
  );
}

// ── BLOCAGES : ce qui bloque ce magasin, ce qui peut le bloquer, ce qui l'a déjà fait. ──
function BlocksSection({ touching, past, rule, settingsErr, active, err, loaded, canBlock, onBlock }: {
  touching: BlockRow[]; past: BlockRow[]; rule: { on: boolean; warnDays: number; lockDays: number } | null; settingsErr: string | null; active: boolean;
  err: string | null; loaded: boolean; canBlock: boolean; onBlock: () => void;
}) {
  const rules = [
    {
      name: 'Synchro périodique',
      state: !active ? 'sans base' : rule === null ? (settingsErr ? 'illisible' : 'lecture…') : rule.on ? 'active' : 'désactivée',
      tone: (rule?.on ? 'ok' : 'ne') as Tone,
      desc: rule?.on ? `Avertissement à J+${rule.warnDays}, blocage à J+${rule.lockDays} sans synchronisation — délais propres à ce magasin (section Réglages).` : 'Délais propres à ce magasin, réglés dans la section Réglages.',
    },
    ...AUTO_BLOCK_RULES.map((r) => ({ name: r.name, state: r.state, tone: (r.state === 'active' ? 'ok' : 'ne') as Tone, desc: r.desc })),
  ];
  return (
    <>
      <Panel title="En cours" actions={canBlock ? <button type="button" className="btn-ghost sm danger" onClick={onBlock}>Poser un blocage</button> : undefined}>
        {err ? <ReadError>Blocages illisibles : {err}</ReadError>
          : !loaded ? <div className="sp-empty">Lecture des blocages…</div>
          : touching.length === 0 ? <EmptyState icon="shield-check" compact title="Aucun blocage sur ce magasin" note="Ni avertissement ni blocage, manuel ou automatique — y compris sur l’abonnement du client." />
          : touching.map((b) => (
            <div key={b.id} className="sp-row" style={{ gridTemplateColumns: 'auto minmax(0,1fr)', alignItems: 'start' }}>
              <Pill tone={b.level === 'lock' ? 'da' : 'wa'}>{b.level === 'lock' ? 'blocage' : 'avertissement'}</Pill>
              <div className="v">{b.label}<div className="note" style={{ color: 'var(--ink-600)' }}>{b.reason}</div><div className="note mono">{dateTimeShort(b.createdAt)} · {b.createdBy}{b.propagated ? '' : ' · pas encore parvenu aux caisses'}</div></div>
            </div>
          ))}
        {touching.length > 0 && <div className="sp-foot"><Link to="/blocages" className="sp-link">Lever ou modifier dans Blocages →</Link></div>}
      </Panel>
      <Panel title="Règles automatiques" note="Ce qui peut bloquer ce magasin sans intervention. Une levée prend effet à la prochaine synchronisation de la caisse.">
        {rules.map((r) => (
          <div key={r.name} className="sp-row act">
            <div className="k" style={{ fontWeight: 600, color: 'var(--ink-900)' }}>{r.name}</div>
            <div className="v" style={{ fontWeight: 400, fontSize: 'var(--t-dense)', color: 'var(--ink-600)' }}>{r.desc}</div>
            <Pill tone={r.tone}>{r.state}</Pill>
          </div>
        ))}
      </Panel>
      <Panel title="Historique des blocages">
        {err ? <ReadError>{err}</ReadError>
          : !loaded ? <div className="sp-empty">Lecture…</div>
          : past.length === 0 ? <div className="sp-empty">Aucun blocage passé sur ce magasin.</div>
          : past.map((b) => (
            <div key={b.id} className="sp-row act">
              <Pill tone={b.level === 'lock' ? 'da' : 'wa'}>{b.level === 'lock' ? 'blocage' : 'avertissement'}</Pill>
              <div className="v" style={{ fontWeight: 400 }}>{b.label}<div className="note">{b.reason}{b.liftReason ? ` · levé : ${b.liftReason}` : ''}{b.liftedBy ? ` (${b.liftedBy})` : ''}</div></div>
              <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{dateTimeShort(b.createdAt)}{b.liftedAt ? ` → ${dateTimeShort(b.liftedAt)}` : ''}</span>
            </div>
          ))}
      </Panel>
    </>
  );
}
